import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { PagedResponse } from '../../core/api/api-contracts';

export const CatalogRunningNumberKeys = {
  inventoryLot: 'InventoryLot',
  inventorySerial: 'InventorySerial',
  customerReference: 'CustomerReference',
  offeringParent: 'Offering',
  catalogOperationsParent: 'CatalogOperations',
  serviceOrderContext: 'service-order'
} as const;

export interface CatalogOffering {
  id: string;
  type: string;
  code: string;
  title: string;
  slug: string;
  status: string;
  visibility: string;
  shortDescription: string;
  fullDescription: string;
  basePrice: number;
  currency: string;
  pricingModel: string;
  fulfillmentModel: string;
  metaTitle: string;
  metaDescription: string;
  salesEnabled: boolean;
  purchaseEnabled: boolean;
  inventoryEnabled: boolean;
  reservationEnabled: boolean;
  rentalEnabled: boolean;
  assetEnabled: boolean;
  maintenanceEnabled: boolean;
  digitalDeliveryEnabled: boolean;
  categoryId?: string | null;
  consumptionClassification?: string;
  nonRequestable?: boolean;
  nonOrderable?: boolean;
  isCodingLocked?: boolean;
  media?: OfferingMediaItem[];
  primaryImageUrl?: string | null;
}

export type OfferingResponse = CatalogOffering;

export interface OfferingMediaItem {
  id: string;
  offeringId: string;
  fileId: string;
  imageUrl: string;
  thumbnailUrl?: string | null;
  altText: string;
  sortOrder: number;
  isPrimary: boolean;
}

export interface CreateOfferingMediaPayload {
  fileId: string;
  altText: string;
}

export interface UpdateOfferingMediaPayload {
  altText: string;
}

export interface ReorderOfferingMediaPayload {
  mediaIds: string[];
}

export interface CategoryResponse {
  id: string;
  tenantId: string;
  parentId: string | null;
  name: string;
  slug: string;
  path: string;
  level: number;
  consumptionClassification: string;
  isCodingLocked: boolean;
  metaTitle: string;
  metaDescription: string;
  imageFileId?: string | null;
}

export interface CatalogWarehouse {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  name: string;
  code: string;
  isActive: boolean;
}

export interface CreateWarehousePayload {
  name: string;
  code: string;
}

export interface UpdateWarehousePayload {
  name: string;
  code: string;
  isActive: boolean;
}

export interface CatalogWarehouseZone {
  id: string;
  tenantId: string;
  warehouseId: string;
  name: string;
  code: string;
}

export interface CatalogWarehouseBin {
  id: string;
  tenantId: string;
  warehouseId: string;
  zoneId: string | null;
  name: string;
  code: string;
  isActive: boolean;
  binType?: string;
}

export interface InventoryKpiDashboard {
  totalOnHandQuantity: number;
  activeReservations: number;
  pendingPutAwayTasks: number;
  pendingPickTasks: number;
  pendingPackShipTasks: number;
  draftMovements: number;
  quarantineBins: number;
  scheduledInboundShipments: number;
  openPickWaves: number;
  quarantinedLots: number;
}

export interface InventoryStockBalance {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  quantity: number;
  lastUpdatedUtc: string;
}

export interface InventoryLedgerEntry {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  idempotencyKey: string;
  operationType: string;
  quantityDelta: number;
  quantityAfter: number;
  referenceNumber: string;
  occurredAtUtc: string;
}

export interface InventoryMovementLine {
  id: string;
  tenantId: string;
  inventoryMovementId: string;
  offeringId: string;
  fromWarehouseId: string | null;
  fromBinId: string | null;
  toWarehouseId: string | null;
  toBinId: string | null;
  quantity: number;
}

export interface InventoryMovement {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  operationType: string;
  status: string;
  idempotencyKey: string;
  referenceNumber: string;
  transferIssueReferenceNumber?: string | null;
  transferReceiptReferenceNumber?: string | null;
  processedAtUtc: string;
  glPostedAt?: string | null;
  glPostingStatus?: string | null;
  glJournalEntryId?: string | null;
  glErrorCode?: string | null;
  lines: InventoryMovementLine[];
}

export interface InventoryMovementPayload {
  offeringId: string;
  quantity: number;
  warehouseId: string | null;
  binId: string | null;
  fromWarehouseId: string | null;
  fromBinId: string | null;
  toWarehouseId: string | null;
  toBinId: string | null;
  idempotencyKey: string;
  referenceNumber: string;
  lotId?: string | null;
  serialId?: string | null;
  applyLotRotation?: boolean;
  rotationPolicy?: string | null;
  fefoOverrideReason?: string | null;
  unitCost?: number | null;
  currencyCode?: string | null;
}

export interface InventoryCountLine {
  id: string;
  tenantId: string;
  inventoryCountSessionId: string;
  offeringId: string;
  binId: string | null;
  expectedQuantity: number;
  countedQuantity: number;
  varianceQuantity: number;
  roundNumber: number;
  isBlindCount: boolean;
  discrepancyReason: string | null;
  lastCountedAtUtc: string | null;
  lastCountedByUserId: string | null;
}

export interface InventoryCountSession {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  warehouseId: string;
  referenceNumber: string;
  status: string;
  createdAtUtc: string;
  closedAtUtc: string | null;
  cancelledAtUtc: string | null;
  closeReason: string | null;
  cancelReason: string | null;
  lines: InventoryCountLine[];
}

export interface InboundShipmentLine {
  id: string;
  offeringId: string;
  expectedQuantity: number;
  receivedQuantity: number;
}

export interface InboundShipment {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  warehouseId: string;
  stagingBinId: string | null;
  referenceNumber: string;
  externalReference: string | null;
  status: string;
  scheduledArrivalUtc: string | null;
  dockDoorCode: string | null;
  createdAtUtc: string;
  lines: InboundShipmentLine[];
}

export interface CreateInboundShipmentPayload {
  warehouseId: string;
  stagingBinId: string | null;
  referenceNumber: string;
  externalReference: string | null;
  scheduledArrivalUtc: string | null;
  dockDoorCode?: string | null;
  lines: { offeringId: string; expectedQuantity: number }[];
}

export interface InventoryRequisitionLine {
  id: string;
  offeringId: string;
  requestedQuantity: number;
  estimatedUnitCost: number | null;
  estimatedTotalCost: number | null;
  currencyCode: string | null;
  supplyOrderLineId: string | null;
}

export interface InventoryRequisition {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  warehouseId: string;
  referenceNumber: string;
  status: string;
  notes: string | null;
  submittedAtUtc: string | null;
  convertedAtUtc: string | null;
  cancelledAtUtc: string | null;
  cancelReason: string | null;
  supplyOrderId: string | null;
  lines: InventoryRequisitionLine[];
  createdAtUtc: string;
}

export interface CreateInventoryRequisitionPayload {
  warehouseId: string;
  referenceNumber: string;
  notes: string | null;
  lines: { offeringId: string; requestedQuantity: number }[];
}

export interface InventorySupplyOrderLine {
  id: string;
  offeringId: string;
  orderedQuantity: number;
  estimatedUnitCost: number | null;
  estimatedTotalCost: number | null;
  currencyCode: string | null;
  sourceRequisitionLineId: string;
}

export interface InventorySupplyOrder {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  warehouseId: string;
  referenceNumber: string;
  status: string;
  sourceRequisitionId: string;
  lines: InventorySupplyOrderLine[];
  createdAtUtc: string;
}

export interface InventoryReplenishmentPolicy {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  reorderPoint: number;
  minimumQuantity: number;
  maximumQuantity: number;
  safetyStock: number;
  leadTimeDays: number;
  isActive: boolean;
  createdAtUtc: string;
}

export interface UpsertInventoryReplenishmentPolicyPayload {
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  reorderPoint: number;
  minimumQuantity: number;
  maximumQuantity: number;
  safetyStock: number;
  leadTimeDays: number;
  isActive: boolean;
}

export interface InventoryReplenishmentBreachItem {
  policyId: string;
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  category: string;
  onHandQuantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  reorderPoint: number;
  minimumQuantity: number;
  maximumQuantity: number;
}

export interface InventoryScanLookup {
  entityType: string;
  entityId: string;
  code: string;
  detail: string;
  warehouseId: string;
}

export interface InventoryLotSuggestion {
  lotId: string;
  lotNumber: string;
  binId: string | null;
  suggestedQuantity: number;
  expiryDate: string | null;
  rotationPolicy: string;
}

export interface PickWave {
  id: string;
  referenceNumber: string;
  status: string;
  reservationIds: string[];
  createdAtUtc: string;
  reservationSummaries?: PickWaveReservationSummary[];
}

export interface PickWaveReservationSummary {
  reservationId: string;
  offeringId: string;
  warehouseId: string;
  sourceType: string;
  sourceReference: string;
  quantity: number;
  status: string;
}

export interface ReceiveInboundLinePayload {
  lineId: string;
  quantity: number;
  stagingBinId: string | null;
  targetBinId: string | null;
  directPutAway: boolean;
  idempotencyKey: string;
}

export interface ReceiveInboundLineResult {
  lineId: string;
  receivedQuantity: number;
  receiptMovementId: string | null;
  putAwayTaskId: string | null;
}

export interface WarehouseTask {
  id: string;
  tenantId: string;
  taskType: string;
  status: string;
  inboundShipmentLineId: string | null;
  offeringId: string;
  warehouseId: string;
  fromBinId: string;
  toBinId: string;
  quantity: number;
  receiptMovementId: string | null;
  completionMovementId: string | null;
  createdAtUtc: string;
}

export interface InventoryReservation {
  id: string;
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  sourceType: string;
  sourceReference: string;
  quantity: number;
  status: string;
  createdAtUtc: string;
}

export interface InventoryAvailability {
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  onHandQuantity: number;
  reservedQuantity: number;
  availableQuantity: number;
}

export interface InventoryCountLineInput {
  offeringId: string;
  binId: string | null;
  countedQuantity: number;
  isBlindCount?: boolean;
  roundNumber?: number;
  discrepancyReason?: string | null;
}

export interface CreateInventoryCountSessionPayload {
  warehouseId: string;
  referenceNumber: string;
  startInCounting?: boolean;
  lines: InventoryCountLineInput[];
}

export interface UpdateInventoryCountRoundPayload {
  lines: InventoryCountLineInput[];
  moveToReview?: boolean;
}

export interface CloseInventoryCountSessionPayload {
  idempotencyKey: string;
  reason: string;
}

export interface InventoryLot {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  lotNumber: string;
  quantity: number;
  manufacturedDate: string | null;
  expiryDate: string | null;
  status: string;
  sourceReferenceNumber: string;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface CreateInventoryLotPayload {
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  lotNumber: string;
  quantity: number;
  manufacturedDate: string | null;
  expiryDate: string | null;
  sourceReferenceNumber: string;
}

export interface InventorySerial {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  offeringId: string;
  lotId: string | null;
  warehouseId: string | null;
  binId: string | null;
  serialNumber: string;
  status: string;
  issuedReferenceNumber: string;
  issuedAtUtc: string | null;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface CreateInventorySerialPayload {
  offeringId: string;
  lotId: string | null;
  warehouseId: string | null;
  binId: string | null;
  serialNumber: string;
}

export interface InventoryTraceabilityNode {
  nodeType: string;
  id: string;
  label: string;
  status: string;
  children: InventoryTraceabilityNode[];
}

export interface InventoryTraceabilityResult {
  nodes: InventoryTraceabilityNode[];
}

export interface InventoryQcRule {
  id: string;
  tenantId: string;
  name: string;
  code: string;
  inspectionType: string;
  checklistJson: string;
  isActive: boolean;
  blocksOnFail: boolean;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface InventoryQcInspection {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  ruleId: string | null;
  offeringId: string;
  lotId: string | null;
  serialId: string | null;
  inspectionType: string;
  status: string;
  referenceNumber: string;
  warehouseId: string | null;
  startedAtUtc: string | null;
  completedAtUtc: string | null;
  resultNotes: string;
  failReasonCode: string;
  checklistResultsJson: string;
  triggeredRecallCampaignId: string | null;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface CreateQcInspectionPayload {
  ruleId: string | null;
  offeringId: string;
  lotId: string | null;
  serialId: string | null;
  inspectionType: string;
  referenceNumber: string;
  warehouseId: string | null;
}

export interface CompleteQcInspectionPayload {
  passed: boolean;
  resultNotes: string;
  failReasonCode: string;
  checklistResultsJson: string;
}

export interface InventoryRecallLine {
  id: string;
  campaignId: string;
  targetType: string;
  lotId: string | null;
  serialId: string | null;
  targetNumber: string;
  status: string;
  resultNotes: string;
  executedAtUtc: string | null;
}

export interface InventoryRecallCampaign {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  campaignNumber: string;
  name: string;
  reasonCode: string;
  description: string;
  status: string;
  offeringId: string | null;
  lotId: string | null;
  executionKey: string;
  executedAtUtc: string | null;
  totalLines: number;
  processedLines: number;
  quarantinedLines: number;
  skippedLines: number;
  simulatedAtUtc: string | null;
  approvedAtUtc: string | null;
  approvedByUserId: string | null;
  customerImpactCount: number;
  warehouseImpactCount: number;
  sourceQcInspectionId: string | null;
  createdAtUtc: string;
  updatedAtUtc: string | null;
  lines?: InventoryRecallLine[];
}

export interface InventoryRecallImpactLine {
  id: string;
  campaignId: string;
  impactType: string;
  offeringId: string;
  lotId: string | null;
  serialId: string | null;
  warehouseId: string | null;
  targetNumber: string;
  referenceNumber: string;
  quantity: number;
  notes: string;
}

export interface CreateRecallCampaignPayload {
  campaignNumber: string;
  name: string;
  reasonCode: string;
  description: string;
  offeringId: string | null;
  lotId: string | null;
  executionKey: string;
}

export interface ExecuteRecallCampaignResult {
  campaign: InventoryRecallCampaign;
  processedInRun: number;
  quarantinedInRun: number;
  skippedInRun: number;
  isComplete: boolean;
}

export interface SimulateRecallCampaignResult {
  campaign: InventoryRecallCampaign;
  customerImpactCount: number;
  warehouseImpactCount: number;
  impactLines: InventoryRecallImpactLine[];
}

export interface RecallImpactResult {
  campaign: InventoryRecallCampaign;
  impactLines: InventoryRecallImpactLine[];
}

export interface InventoryRecallAuditEvent {
  id: string;
  campaignId: string;
  eventType: string;
  actorUserId: string | null;
  message: string;
  payloadJson: string;
  occurredAtUtc: string;
}

export interface RecallControlRoomSummary {
  draftCount: number;
  pendingApprovalCount: number;
  approvedCount: number;
  executingCount: number;
  completedCount: number;
  cancelledCount: number;
  activeCampaigns: RecallControlRoomCampaign[];
}

export interface RecallControlRoomCampaign {
  id: string;
  campaignNumber: string;
  status: string;
  totalLines: number;
  processedLines: number;
  customerImpactCount: number;
  progressPercent: number;
}

export interface ExpiringLotAlert {
  lotId: string;
  lotNumber: string;
  offeringId: string;
  expiryDate: string | null;
  daysUntilExpiry: number;
  quantity: number;
  status: string;
}

export interface TrackingDashboardSummary {
  totalLots: number;
  totalSerials: number;
  quarantinedLots: number;
  quarantinedSerials: number;
  expiringWithin30Days: number;
  expiringLots: ExpiringLotAlert[];
}

export interface QcWorkbenchSummary {
  pendingCount: number;
  inProgressCount: number;
  failedCount: number;
  passedCount: number;
}

export interface ServiceOperationsSummary {
  draftCount: number;
  scheduledCount: number;
  assignedCount: number;
  inProgressCount: number;
  pendingCustomerApprovalCount: number;
  slaBreachedCount: number;
}

export interface RmaWorkbenchSummary {
  submittedCount: number;
  diagnosedCount: number;
  pendingApprovalCount: number;
  resolvedCount: number;
}

export interface OperationsControlRoomSummary {
  tracking: TrackingDashboardSummary;
  quality: QcWorkbenchSummary;
  service: ServiceOperationsSummary;
  rma: RmaWorkbenchSummary;
  recall: RecallControlRoomSummary;
  assets: FixedAssetsOperationsSummary;
  rental: RentalOperationsSummary;
  cmms: CmmsOperationsSummary;
}

export interface FixedAssetsOperationsSummary {
  registeredCount: number;
  inServiceCount: number;
  underMaintenanceCount: number;
  disposedCount: number;
  pendingDisposalApprovalCount: number;
  draftDepreciationRunCount: number;
}

export interface RentalOperationsSummary {
  availableUnitCount: number;
  rentedUnitCount: number;
  maintenanceUnitCount: number;
  activeAgreementCount: number;
  pendingDamageApprovalCount: number;
}

export interface CmmsOperationsSummary {
  activePlanCount: number;
  openWorkOrderCount: number;
  inProgressWorkOrderCount: number;
  openDowntimeCount: number;
}

export interface DispatchAssignment {
  assignmentId: string;
  resourceId: string;
  resourceCode: string;
  resourceName: string;
  isPrimary: boolean;
}

export interface DispatchBoardEntry {
  orderId: string;
  orderNumber: string;
  title: string;
  status: string;
  priority: string;
  scheduledStartUtc: string | null;
  scheduledEndUtc: string | null;
  isSlaBreached: boolean;
  assignments: DispatchAssignment[];
}

export interface TechnicianWorkQueueEntry {
  orderId: string;
  orderNumber: string;
  title: string;
  status: string;
  priority: string;
  scheduledStartUtc: string | null;
  scheduledEndUtc: string | null;
  isSlaBreached: boolean;
  customerReferenceNumber: string;
  isPrimaryAssignment: boolean;
}

export interface InventoryRecallExecutionJob {
  id: string;
  campaignId: string;
  status: string;
  batchSize: number;
  runCount: number;
}

export interface ServiceOrder {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  orderNumber: string;
  title: string;
  description: string;
  offeringId: string | null;
  serialId: string | null;
  customerReferenceNumber: string;
  status: string;
  priority: string;
  slaPolicyCode: string;
  scheduledStartUtc: string | null;
  scheduledEndUtc: string | null;
  startedAtUtc: string | null;
  completedAtUtc: string | null;
  completionNotes: string;
  checklistResultsJson: string;
  createdAtUtc: string;
  updatedAtUtc: string | null;
  assignments?: ServiceAssignment[];
  consumptions?: ServiceConsumption[];
}

export interface ServiceAssignment {
  id: string;
  serviceOrderId: string;
  resourceId: string;
  isPrimary: boolean;
  assignedAtUtc: string;
}

export interface ServiceConsumption {
  id: string;
  serviceOrderId: string;
  offeringId: string;
  lotId: string | null;
  serialId: string | null;
  quantity: number;
  referenceNumber: string;
  createdAtUtc: string;
}

export interface ServiceResource {
  id: string;
  tenantId: string;
  name: string;
  code: string;
  resourceType: string;
  isActive: boolean;
}

export interface CreateServiceResourcePayload {
  name: string;
  code: string;
  resourceType: string;
}

export interface WarrantyContract {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  contractNumber: string;
  customerReferenceNumber: string;
  offeringId: string | null;
  serialId: string | null;
  startDateUtc: string;
  endDateUtc: string;
  coverageType: string;
  isActive: boolean;
  termsJson: string;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface WarrantyEntitlement {
  lookupReference: string;
  isEntitled: boolean;
  matchingContracts: WarrantyContract[];
  serialId: string | null;
  serialNumber: string | null;
  serviceOrderId: string | null;
  serviceOrderNumber: string | null;
}

export interface CreateWarrantyContractPayload {
  contractNumber: string;
  customerReferenceNumber: string;
  offeringId: string | null;
  serialId: string | null;
  startDateUtc: string;
  endDateUtc: string;
  coverageType: string;
  termsJson: string;
}

export interface RmaRequest {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  requestNumber: string;
  customerReferenceNumber: string;
  offeringId: string | null;
  serialId: string | null;
  serviceOrderId: string | null;
  warrantyContractId: string | null;
  status: string;
  reasonCode: string;
  description: string;
  diagnosisResultCode: string;
  diagnosisNotes: string;
  diagnosedAtUtc: string | null;
  isUnderWarranty: boolean;
  createdAtUtc: string;
  updatedAtUtc: string | null;
  decisions?: RmaDecision[];
}

export interface RmaDecision {
  id: string;
  rmaRequestId: string;
  decisionType: string;
  status: string;
  resolutionNotes: string;
  refundAmount: number | null;
  replacementSerialId: string | null;
  approvedByUserId: string | null;
  approvedAtUtc: string | null;
  appliedAtUtc: string | null;
  createdAtUtc: string;
}

export interface CreateRmaRequestPayload {
  requestNumber: string;
  customerReferenceNumber: string;
  offeringId: string | null;
  serialId: string | null;
  serviceOrderId: string | null;
  reasonCode: string;
  description: string;
}

export interface DiagnoseRmaPayload {
  diagnosisResultCode: string;
  diagnosisNotes: string;
}

export interface ResolveRmaPayload {
  decisionType: string;
  resolutionNotes: string;
  refundAmount: number | null;
  replacementSerialId: string | null;
}

export interface CreateServiceOrderPayload {
  orderNumber: string;
  title: string;
  description: string;
  offeringId: string | null;
  serialId: string | null;
  customerReferenceNumber: string;
  priority: string;
  slaPolicyCode: string;
}

export interface CreateOfferingPayload {
  type: string;
  code: string;
  title: string;
  slug: string;
  shortDescription: string;
  fullDescription: string;
  basePrice: number;
  currency: string;
  pricingModel: string;
  fulfillmentModel: string;
  salesEnabled: boolean;
  purchaseEnabled: boolean;
  inventoryEnabled: boolean;
  reservationEnabled: boolean;
  rentalEnabled: boolean;
  assetEnabled: boolean;
  maintenanceEnabled: boolean;
  digitalDeliveryEnabled: boolean;
  visibility: string;
  metaTitle: string;
  metaDescription: string;
  categoryId?: string | null;
  consumptionClassification?: string;
  nonRequestable?: boolean;
  nonOrderable?: boolean;
}

export interface UpdateBehaviorFlagsPayload {
  salesEnabled: boolean;
  purchaseEnabled: boolean;
  inventoryEnabled: boolean;
  reservationEnabled: boolean;
  rentalEnabled: boolean;
  assetEnabled: boolean;
  maintenanceEnabled: boolean;
  digitalDeliveryEnabled: boolean;
}

export type UpdateOfferingPayload = CreateOfferingPayload;

export interface OfferingProfile {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  offeringId: string;
  profileType: string;
  payloadJson: string;
  isValid: boolean;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface OfferingProfileValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface AttributeDefinition {
  id: string;
  tenantId: string;
  key: string;
  displayName: string;
  type: string;
}

export interface OfferingAttributeValue {
  id: string;
  attributeDefinitionId: string;
  value: string;
}

export interface OfferingAttributeAssignment {
  id: string;
  attributeDefinitionId: string;
  key: string;
  displayName: string;
  type: string;
  value: string;
}

@Injectable({ providedIn: 'root' })
export class CatalogService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = environment.apiBaseUrl;

  listOfferings(): Observable<CatalogOffering[]> {
    return this.http.get<CatalogOffering[]>(`${this.apiBase}/api/v1/catalog/offerings`);
  }

  listCategories(): Observable<CategoryResponse[]> {
    return this.http.get<CategoryResponse[]>(`${this.apiBase}/api/v1/catalog/categories`);
  }

  createCategory(payload: {
    name: string;
    slug: string;
    metaTitle?: string;
    metaDescription?: string;
    consumptionClassification?: string;
    parentId?: string | null;
  }): Observable<CategoryResponse> {
    return this.http.post<CategoryResponse>(`${this.apiBase}/api/v1/catalog/categories`, {
      name: payload.name,
      slug: payload.slug,
      metaTitle: payload.metaTitle ?? payload.name,
      metaDescription: payload.metaDescription ?? '',
      consumptionClassification: payload.consumptionClassification ?? 'Unspecified',
      parentId: payload.parentId ?? null
    });
  }

  updateCategory(id: string, payload: {
    name: string;
    slug: string;
    metaTitle?: string;
    metaDescription?: string;
    consumptionClassification: string;
    parentId?: string | null;
  }): Observable<CategoryResponse> {
    return this.http.put<CategoryResponse>(`${this.apiBase}/api/v1/catalog/categories/${id}`, {
      name: payload.name,
      slug: payload.slug,
      metaTitle: payload.metaTitle ?? payload.name,
      metaDescription: payload.metaDescription ?? '',
      consumptionClassification: payload.consumptionClassification,
      parentId: payload.parentId ?? null
    });
  }

  deleteCategory(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiBase}/api/v1/catalog/categories/${id}`);
  }

  uploadCategoryImage(id: string, file: File): Observable<CategoryResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<CategoryResponse>(`${this.apiBase}/api/v1/catalog/categories/${id}/image`, formData);
  }

  removeCategoryImage(id: string): Observable<CategoryResponse> {
    return this.http.delete<CategoryResponse>(`${this.apiBase}/api/v1/catalog/categories/${id}/image`);
  }

  categoryImagePreviewUrl(category: CategoryResponse): string | null {
    if (!category.imageFileId) {
      return null;
    }
    return `${this.apiBase}/api/v1/catalog/categories/${category.id}/image/content?v=${category.imageFileId}`;
  }

  getCategory(id: string): Observable<CategoryResponse> {
    return this.http.get<CategoryResponse>(`${this.apiBase}/api/v1/catalog/categories/${id}`);
  }

  getOffering(id: string): Observable<CatalogOffering> {
    return this.http.get<CatalogOffering>(`${this.apiBase}/api/v1/catalog/offerings/${id}`);
  }

  createOffering(payload: CreateOfferingPayload): Observable<CatalogOffering> {
    return this.http.post<CatalogOffering>(`${this.apiBase}/api/v1/catalog/offerings`, payload);
  }

  updateOffering(id: string, payload: UpdateOfferingPayload): Observable<CatalogOffering> {
    return this.http.put<CatalogOffering>(`${this.apiBase}/api/v1/catalog/offerings/${id}`, payload);
  }

  updateOfferingStatus(id: string, status: string): Observable<void> {
    return this.http.patch<void>(`${this.apiBase}/api/v1/catalog/offerings/${id}/status`, { status });
  }

  updateBehaviorFlags(id: string, payload: UpdateBehaviorFlagsPayload): Observable<void> {
    return this.http.patch<void>(`${this.apiBase}/api/v1/catalog/offerings/${id}/behavior-flags`, payload);
  }

  getOfferingProfile(id: string, profileType: string): Observable<OfferingProfile> {
    return this.http.get<OfferingProfile>(`${this.apiBase}/api/v1/catalog/offerings/${id}/profiles/${profileType}`);
  }

  upsertOfferingProfile(id: string, profileType: string, payloadJson: string): Observable<OfferingProfile> {
    return this.http.put<OfferingProfile>(`${this.apiBase}/api/v1/catalog/offerings/${id}/profiles/${profileType}`, { payloadJson });
  }

  validateOfferingProfile(id: string, profileType: string, payloadJson: string): Observable<OfferingProfileValidationResult> {
    return this.http.post<OfferingProfileValidationResult>(`${this.apiBase}/api/v1/catalog/offerings/${id}/profiles/validate`, {
      profileType,
      payloadJson
    });
  }

  listAttributeDefinitions(): Observable<AttributeDefinition[]> {
    return this.http.get<AttributeDefinition[]>(`${this.apiBase}/api/v1/catalog/attributes`);
  }

  getOfferingAttributes(id: string): Observable<OfferingAttributeAssignment[]> {
    return this.http.get<OfferingAttributeAssignment[]>(`${this.apiBase}/api/v1/catalog/offerings/${id}/attributes`);
  }

  createAttributeDefinition(payload: {
    key: string;
    displayName: string;
    type: string;
    isFilterable: boolean;
    isSearchable: boolean;
  }): Observable<AttributeDefinition> {
    return this.http.post<AttributeDefinition>(`${this.apiBase}/api/v1/catalog/attributes`, payload);
  }

  updateOfferingAttributes(id: string, values: { attributeDefinitionId: string; value: string }[]): Observable<OfferingAttributeAssignment[]> {
    return this.http.put<OfferingAttributeAssignment[]>(`${this.apiBase}/api/v1/catalog/offerings/${id}/attributes`, { values });
  }

  publishOffering(id: string): Observable<void> {
    return this.http.post<void>(`${this.apiBase}/api/v1/catalog/offerings/${id}/publish`, {});
  }

  unpublishOffering(id: string): Observable<void> {
    return this.http.post<void>(`${this.apiBase}/api/v1/catalog/offerings/${id}/unpublish`, {});
  }

  listOfferingMedia(offeringId: string): Observable<OfferingMediaItem[]> {
    return this.http.get<OfferingMediaItem[]>(`${this.apiBase}/api/v1/catalog/offerings/${offeringId}/media`);
  }

  addOfferingMedia(offeringId: string, payload: CreateOfferingMediaPayload): Observable<OfferingMediaItem> {
    return this.http.post<OfferingMediaItem>(`${this.apiBase}/api/v1/catalog/offerings/${offeringId}/media`, payload);
  }

  uploadOfferingMedia(offeringId: string, file: File, altText = ''): Observable<OfferingMediaItem> {
    const formData = new FormData();
    formData.append('file', file);
    if (altText.trim()) {
      formData.append('altText', altText.trim());
    }
    return this.http.post<OfferingMediaItem>(`${this.apiBase}/api/v1/catalog/offerings/${offeringId}/media/upload`, formData);
  }

  updateOfferingMedia(offeringId: string, mediaId: string, payload: UpdateOfferingMediaPayload): Observable<OfferingMediaItem> {
    return this.http.patch<OfferingMediaItem>(`${this.apiBase}/api/v1/catalog/offerings/${offeringId}/media/${mediaId}`, payload);
  }

  reorderOfferingMedia(offeringId: string, payload: ReorderOfferingMediaPayload): Observable<OfferingMediaItem[]> {
    return this.http.post<OfferingMediaItem[]>(`${this.apiBase}/api/v1/catalog/offerings/${offeringId}/media/reorder`, payload);
  }

  deleteOfferingMedia(offeringId: string, mediaId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiBase}/api/v1/catalog/offerings/${offeringId}/media/${mediaId}`);
  }

  resolveMediaContentUrl(imageUrl: string): string {
    if (!imageUrl) {
      return '';
    }
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    const normalized = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
    return `${this.apiBase}${normalized}`;
  }

  fetchMediaContent(imageUrl: string): Observable<Blob> {
    return this.http.get(this.resolveMediaContentUrl(imageUrl), { responseType: 'blob' });
  }

  listWarehouses(): Observable<CatalogWarehouse[]> {
    return this.http.get<CatalogWarehouse[]>(`${this.apiBase}/api/v1/inventory/warehouses`);
  }

  getWarehouse(id: string): Observable<CatalogWarehouse> {
    return this.http.get<CatalogWarehouse>(`${this.apiBase}/api/v1/inventory/warehouses/${id}`);
  }

  createWarehouse(payload: CreateWarehousePayload): Observable<CatalogWarehouse> {
    return this.http.post<CatalogWarehouse>(`${this.apiBase}/api/v1/inventory/warehouses`, payload);
  }

  updateWarehouse(id: string, payload: UpdateWarehousePayload): Observable<CatalogWarehouse> {
    return this.http.put<CatalogWarehouse>(`${this.apiBase}/api/v1/inventory/warehouses/${id}`, payload);
  }

  listWarehouseZones(warehouseId?: string): Observable<CatalogWarehouseZone[]> {
    const query = warehouseId ? `?warehouseId=${warehouseId}` : '';
    return this.http.get<CatalogWarehouseZone[]>(`${this.apiBase}/api/v1/inventory/warehouse-zones${query}`);
  }

  createWarehouseZone(payload: { warehouseId: string; name: string; code: string }): Observable<CatalogWarehouseZone> {
    return this.http.post<CatalogWarehouseZone>(`${this.apiBase}/api/v1/inventory/warehouse-zones`, payload);
  }

  listWarehouseBins(warehouseId?: string, zoneId?: string): Observable<CatalogWarehouseBin[]> {
    const params = new URLSearchParams();
    if (warehouseId) params.set('warehouseId', warehouseId);
    if (zoneId) params.set('zoneId', zoneId);
    const query = params.toString();
    return this.http.get<CatalogWarehouseBin[]>(`${this.apiBase}/api/v1/inventory/warehouse-bins${query ? `?${query}` : ''}`);
  }

  createWarehouseBin(payload: { warehouseId: string; zoneId: string | null; name: string; code: string; binType?: string | null }): Observable<CatalogWarehouseBin> {
    return this.http.post<CatalogWarehouseBin>(`${this.apiBase}/api/v1/inventory/warehouse-bins`, payload);
  }

  getInventoryKpiDashboard(): Observable<InventoryKpiDashboard> {
    return this.http.get<InventoryKpiDashboard>(`${this.apiBase}/api/v1/inventory/kpi-dashboard`);
  }

  submitInventoryMovementDraft(operationType: string, payload: InventoryMovementPayload): Observable<InventoryMovement> {
    return this.http.post<InventoryMovement>(`${this.apiBase}/api/v1/inventory/movements/${operationType}/draft`, payload);
  }

  approveInventoryMovementDraft(id: string): Observable<InventoryMovement> {
    return this.http.post<InventoryMovement>(`${this.apiBase}/api/v1/inventory/movements/${id}/approve`, {});
  }

  listInventoryMovements(operationType?: string, status?: string, offeringId?: string): Observable<InventoryMovement[]> {
    const params = new URLSearchParams();
    if (operationType) params.set('operationType', operationType);
    if (status) params.set('status', status);
    if (offeringId) params.set('offeringId', offeringId);
    const query = params.toString();
    return this.http.get<InventoryMovement[]>(`${this.apiBase}/api/v1/inventory/movements${query ? `?${query}` : ''}`);
  }

  listStockBalances(): Observable<InventoryStockBalance[]> {
    return this.http.get<InventoryStockBalance[]>(`${this.apiBase}/api/v1/inventory/stock-balances`);
  }

  listLedger(): Observable<InventoryLedgerEntry[]> {
    return this.http.get<InventoryLedgerEntry[]>(`${this.apiBase}/api/v1/inventory/ledger`);
  }

  getInventoryMovement(id: string): Observable<InventoryMovement> {
    return this.http.get<InventoryMovement>(`${this.apiBase}/api/v1/inventory/movements/${id}`);
  }

  retryInventoryMovementGlPosting(id: string): Observable<InventoryMovement> {
    return this.http.post<InventoryMovement>(`${this.apiBase}/api/v1/inventory/movements/${id}/retry-gl-posting`, {});
  }

  postInventoryMovement(operationType: string, payload: InventoryMovementPayload): Observable<InventoryMovement> {
    return this.http.post<InventoryMovement>(`${this.apiBase}/api/v1/inventory/movements/${operationType}`, payload);
  }

  listInventoryCountSessions(): Observable<InventoryCountSession[]> {
    return this.http.get<InventoryCountSession[]>(`${this.apiBase}/api/v1/inventory/count-sessions`);
  }

  createInventoryCountSession(payload: CreateInventoryCountSessionPayload): Observable<InventoryCountSession> {
    return this.http.post<InventoryCountSession>(`${this.apiBase}/api/v1/inventory/count-sessions`, payload);
  }

  startInventoryCountSession(id: string): Observable<void> {
    return this.http.post<void>(`${this.apiBase}/api/v1/inventory/count-sessions/${id}/start-counting`, {});
  }

  updateInventoryCountRound(id: string, payload: UpdateInventoryCountRoundPayload): Observable<InventoryCountSession> {
    return this.http.post<InventoryCountSession>(`${this.apiBase}/api/v1/inventory/count-sessions/${id}/count-round`, payload);
  }

  moveInventoryCountSessionToReview(id: string): Observable<void> {
    return this.http.post<void>(`${this.apiBase}/api/v1/inventory/count-sessions/${id}/review`, {});
  }

  closeInventoryCountSession(id: string, payload: CloseInventoryCountSessionPayload): Observable<void> {
    return this.http.post<void>(`${this.apiBase}/api/v1/inventory/count-sessions/${id}/close`, payload);
  }

  cancelInventoryCountSession(id: string, reason: string): Observable<void> {
    return this.http.post<void>(`${this.apiBase}/api/v1/inventory/count-sessions/${id}/cancel`, { reason });
  }

  listInboundShipments(status?: string): Observable<InboundShipment[]> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.http.get<InboundShipment[]>(`${this.apiBase}/api/v1/inventory/inbound-shipments${query}`);
  }

  getInboundShipment(id: string): Observable<InboundShipment> {
    return this.http.get<InboundShipment>(`${this.apiBase}/api/v1/inventory/inbound-shipments/${id}`);
  }

  createInboundShipment(payload: CreateInboundShipmentPayload): Observable<InboundShipment> {
    return this.http.post<InboundShipment>(`${this.apiBase}/api/v1/inventory/inbound-shipments`, payload);
  }

  scheduleInboundShipment(id: string): Observable<void> {
    return this.http.post<void>(`${this.apiBase}/api/v1/inventory/inbound-shipments/${id}/schedule`, {});
  }

  cancelInboundShipment(id: string): Observable<void> {
    return this.http.post<void>(`${this.apiBase}/api/v1/inventory/inbound-shipments/${id}/cancel`, {});
  }

  receiveInboundLine(shipmentId: string, payload: ReceiveInboundLinePayload): Observable<ReceiveInboundLineResult> {
    return this.http.post<ReceiveInboundLineResult>(
      `${this.apiBase}/api/v1/inventory/inbound-shipments/${shipmentId}/receive`,
      payload);
  }

  listInventoryRequisitions(params?: { status?: string; warehouseId?: string; pageNumber?: number; pageSize?: number }): Observable<PagedResponse<InventoryRequisition>> {
    const search = new URLSearchParams();
    if (params?.status) search.set('status', params.status);
    if (params?.warehouseId) search.set('warehouseId', params.warehouseId);
    search.set('pageNumber', String(params?.pageNumber ?? 1));
    search.set('pageSize', String(params?.pageSize ?? 15));
    const query = search.toString();
    return this.http.get<PagedResponse<InventoryRequisition>>(`${this.apiBase}/api/v1/inventory/requisitions?${query}`);
  }

  getInventoryRequisition(id: string): Observable<InventoryRequisition> {
    return this.http.get<InventoryRequisition>(`${this.apiBase}/api/v1/inventory/requisitions/${id}`);
  }

  createInventoryRequisition(payload: CreateInventoryRequisitionPayload): Observable<InventoryRequisition> {
    return this.http.post<InventoryRequisition>(`${this.apiBase}/api/v1/inventory/requisitions`, payload);
  }

  updateInventoryRequisition(id: string, payload: Omit<CreateInventoryRequisitionPayload, 'referenceNumber'> & { warehouseId: string }): Observable<InventoryRequisition> {
    return this.http.put<InventoryRequisition>(`${this.apiBase}/api/v1/inventory/requisitions/${id}`, payload);
  }

  submitInventoryRequisition(id: string): Observable<InventoryRequisition> {
    return this.http.post<InventoryRequisition>(`${this.apiBase}/api/v1/inventory/requisitions/${id}/submit`, {});
  }

  cancelInventoryRequisition(id: string, reason: string): Observable<InventoryRequisition> {
    return this.http.post<InventoryRequisition>(`${this.apiBase}/api/v1/inventory/requisitions/${id}/cancel`, { reason });
  }

  convertInventoryRequisition(id: string, idempotencyKey: string): Observable<{ requisition: InventoryRequisition; supplyOrder: InventorySupplyOrder }> {
    return this.http.post<{ requisition: InventoryRequisition; supplyOrder: InventorySupplyOrder }>(
      `${this.apiBase}/api/v1/inventory/requisitions/${id}/convert`,
      { idempotencyKey });
  }

  getInventorySupplyOrder(id: string): Observable<InventorySupplyOrder> {
    return this.http.get<InventorySupplyOrder>(`${this.apiBase}/api/v1/inventory/supply-orders/${id}`);
  }

  listInventoryReplenishmentPolicies(warehouseId?: string, offeringId?: string, isActive?: boolean): Observable<InventoryReplenishmentPolicy[]> {
    const params = new URLSearchParams();
    if (warehouseId) params.set('warehouseId', warehouseId);
    if (offeringId) params.set('offeringId', offeringId);
    if (isActive !== undefined) params.set('isActive', String(isActive));
    const query = params.toString();
    return this.http.get<InventoryReplenishmentPolicy[]>(`${this.apiBase}/api/v1/inventory/replenishment-policies${query ? `?${query}` : ''}`);
  }

  getInventoryReplenishmentPolicy(id: string): Observable<InventoryReplenishmentPolicy> {
    return this.http.get<InventoryReplenishmentPolicy>(`${this.apiBase}/api/v1/inventory/replenishment-policies/${id}`);
  }

  createInventoryReplenishmentPolicy(payload: UpsertInventoryReplenishmentPolicyPayload): Observable<InventoryReplenishmentPolicy> {
    return this.http.post<InventoryReplenishmentPolicy>(`${this.apiBase}/api/v1/inventory/replenishment-policies`, payload);
  }

  updateInventoryReplenishmentPolicy(id: string, payload: Omit<UpsertInventoryReplenishmentPolicyPayload, 'offeringId' | 'warehouseId' | 'binId'>): Observable<InventoryReplenishmentPolicy> {
    return this.http.put<InventoryReplenishmentPolicy>(`${this.apiBase}/api/v1/inventory/replenishment-policies/${id}`, payload);
  }

  listInventoryReplenishmentBreaches(warehouseId?: string, category?: string): Observable<{ items: InventoryReplenishmentBreachItem[] }> {
    const params = new URLSearchParams();
    if (warehouseId) params.set('warehouseId', warehouseId);
    if (category) params.set('category', category);
    const query = params.toString();
    return this.http.get<{ items: InventoryReplenishmentBreachItem[] }>(`${this.apiBase}/api/v1/inventory/replenishment-breaches${query ? `?${query}` : ''}`);
  }

  listWarehouseTasks(status?: string, taskType?: string, taskId?: string): Observable<WarehouseTask[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (taskType) params.set('taskType', taskType);
    if (taskId) params.set('taskId', taskId);
    const query = params.toString();
    return this.http.get<WarehouseTask[]>(`${this.apiBase}/api/v1/inventory/warehouse-tasks${query ? `?${query}` : ''}`);
  }

  completePutAwayTask(taskId: string, idempotencyKey: string): Observable<WarehouseTask> {
    return this.http.post<WarehouseTask>(
      `${this.apiBase}/api/v1/inventory/warehouse-tasks/${taskId}/complete-put-away`,
      { idempotencyKey });
  }

  completePickTask(taskId: string, idempotencyKey: string): Observable<WarehouseTask> {
    return this.http.post<WarehouseTask>(
      `${this.apiBase}/api/v1/inventory/warehouse-tasks/${taskId}/complete-pick`,
      { idempotencyKey });
  }

  listInventoryReservations(status?: string, reservationId?: string): Observable<InventoryReservation[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (reservationId) params.set('reservationId', reservationId);
    const query = params.toString();
    return this.http.get<InventoryReservation[]>(`${this.apiBase}/api/v1/inventory/reservations${query ? `?${query}` : ''}`);
  }

  createInventoryReservation(payload: {
    offeringId: string;
    warehouseId: string;
    binId: string | null;
    sourceType: string;
    sourceReference: string;
    quantity: number;
  }): Observable<InventoryReservation> {
    return this.http.post<InventoryReservation>(`${this.apiBase}/api/v1/inventory/reservations`, payload);
  }

  releaseInventoryReservation(id: string): Observable<void> {
    return this.http.post<void>(`${this.apiBase}/api/v1/inventory/reservations/${id}/release`, {});
  }

  createPickTaskFromReservation(reservationId: string): Observable<WarehouseTask> {
    return this.http.post<WarehouseTask>(`${this.apiBase}/api/v1/inventory/reservations/${reservationId}/create-pick-task`, {});
  }

  getInventoryAvailability(offeringId: string, warehouseId: string, binId?: string | null): Observable<InventoryAvailability> {
    const params = new URLSearchParams({ offeringId, warehouseId });
    if (binId) params.set('binId', binId);
    return this.http.get<InventoryAvailability>(`${this.apiBase}/api/v1/inventory/availability?${params}`);
  }

  scanInventoryCode(code: string, warehouseId?: string | null): Observable<InventoryScanLookup> {
    const params = new URLSearchParams({ code });
    if (warehouseId) params.set('warehouseId', warehouseId);
    return this.http.get<InventoryScanLookup>(`${this.apiBase}/api/v1/inventory/scan?${params}`);
  }

  getInventoryLotSuggestions(
    offeringId: string,
    warehouseId: string,
    quantity: number,
    binId?: string | null,
    rotationPolicy?: string
  ): Observable<InventoryLotSuggestion[]> {
    const params = new URLSearchParams({
      offeringId,
      warehouseId,
      quantity: String(quantity)
    });
    if (binId) params.set('binId', binId);
    if (rotationPolicy) params.set('rotationPolicy', rotationPolicy);
    return this.http.get<InventoryLotSuggestion[]>(`${this.apiBase}/api/v1/inventory/lot-suggestions?${params}`);
  }

  listPickWaves(): Observable<PickWave[]> {
    return this.http.get<PickWave[]>(`${this.apiBase}/api/v1/inventory/pick-waves`);
  }

  getPickWave(id: string): Observable<PickWave> {
    return this.http.get<PickWave>(`${this.apiBase}/api/v1/inventory/pick-waves/${id}`);
  }

  createPickWave(referenceNumber: string, reservationIds: string[]): Observable<PickWave> {
    return this.http.post<PickWave>(`${this.apiBase}/api/v1/inventory/pick-waves`, { referenceNumber, reservationIds });
  }

  releasePickWave(id: string): Observable<{ wave: PickWave; pickTasks: WarehouseTask[] }> {
    return this.http.post<{ wave: PickWave; pickTasks: WarehouseTask[] }>(`${this.apiBase}/api/v1/inventory/pick-waves/${id}/release`, {});
  }

  setInboundDockAppointment(id: string, dockDoorCode: string | null, appointmentUtc: string | null): Observable<void> {
    return this.http.post<void>(`${this.apiBase}/api/v1/inventory/inbound-shipments/${id}/dock-appointment`, {
      dockDoorCode,
      appointmentUtc
    });
  }

  completePackTask(taskId: string): Observable<WarehouseTask> {
    return this.http.post<WarehouseTask>(`${this.apiBase}/api/v1/inventory/warehouse-tasks/${taskId}/complete-pack`, {});
  }

  createShipTaskFromPack(packTaskId: string): Observable<WarehouseTask> {
    return this.http.post<WarehouseTask>(`${this.apiBase}/api/v1/inventory/warehouse-tasks/${packTaskId}/create-ship-task`, {});
  }

  completeShipTask(taskId: string, payload?: ConfirmOutboundShipmentPayload): Observable<{ shipment: OutboundShipment | null; task: WarehouseTask }> {
    return this.http.post<{ shipment: OutboundShipment | null; task: WarehouseTask }>(
      `${this.apiBase}/api/v1/inventory/warehouse-tasks/${taskId}/complete-ship`,
      payload ?? {}
    );
  }

  confirmShipDispatch(taskId: string, payload: ConfirmOutboundShipmentPayload): Observable<{ shipment: OutboundShipment | null; task: WarehouseTask }> {
    return this.http.post<{ shipment: OutboundShipment | null; task: WarehouseTask }>(
      `${this.apiBase}/api/v1/inventory/warehouse-tasks/${taskId}/confirm-ship-dispatch`,
      payload
    );
  }

  listOutboundShipments(status?: string): Observable<OutboundShipment[]> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.http.get<OutboundShipment[]>(`${this.apiBase}/api/v1/inventory/outbound-shipments${query}`);
  }

  listCarrierWebhookEvents(): Observable<CarrierWebhookEvent[]> {
    return this.http.get<CarrierWebhookEvent[]>(`${this.apiBase}/api/v1/inventory/carrier-webhook-events`);
  }

  reserveWorkOrderParts(id: string, payload: ReserveWorkOrderPartsPayload): Observable<SparePartReservation> {
    return this.http.post<SparePartReservation>(`${this.apiBase}/api/v1/cmms/work-orders/${id}/reserve-parts`, payload);
  }

  releaseWorkOrderParts(id: string, sparePartReservationId: string): Observable<void> {
    return this.http.post<void>(`${this.apiBase}/api/v1/cmms/work-orders/${id}/release-parts`, { sparePartReservationId });
  }

  previewWorkOrderBom(id: string, parentOfferingId: string, parentQuantity: number): Observable<CmmsBomPreview> {
    const params = new URLSearchParams({ parentOfferingId, parentQuantity: String(parentQuantity) });
    return this.http.get<CmmsBomPreview>(`${this.apiBase}/api/v1/cmms/work-orders/${id}/bom-preview?${params.toString()}`);
  }

  explodeWorkOrderBom(id: string, payload: ExplodeWorkOrderBomPayload): Observable<SparePartReservation[]> {
    return this.http.post<SparePartReservation[]>(`${this.apiBase}/api/v1/cmms/work-orders/${id}/explode-bom`, payload);
  }

  listInventoryLots(offeringId?: string, warehouseId?: string, status?: string): Observable<InventoryLot[]> {
    const params = new URLSearchParams();
    if (offeringId) params.set('offeringId', offeringId);
    if (warehouseId) params.set('warehouseId', warehouseId);
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<InventoryLot[]>(`${this.apiBase}/api/v1/inventory/lots${query ? `?${query}` : ''}`);
  }

  getInventoryLot(id: string): Observable<InventoryLot> {
    return this.http.get<InventoryLot>(`${this.apiBase}/api/v1/inventory/lots/${id}`);
  }

  createInventoryLot(payload: CreateInventoryLotPayload): Observable<InventoryLot> {
    return this.http.post<InventoryLot>(`${this.apiBase}/api/v1/inventory/lots`, payload);
  }

  updateInventoryLotStatus(id: string, status: string): Observable<InventoryLot> {
    return this.http.post<InventoryLot>(`${this.apiBase}/api/v1/inventory/lots/${id}/status`, { status });
  }

  listInventorySerials(offeringId?: string, lotId?: string, status?: string): Observable<InventorySerial[]> {
    const params = new URLSearchParams();
    if (offeringId) params.set('offeringId', offeringId);
    if (lotId) params.set('lotId', lotId);
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<InventorySerial[]>(`${this.apiBase}/api/v1/inventory/serials${query ? `?${query}` : ''}`);
  }

  getInventorySerial(id: string): Observable<InventorySerial> {
    return this.http.get<InventorySerial>(`${this.apiBase}/api/v1/inventory/serials/${id}`);
  }

  createInventorySerial(payload: CreateInventorySerialPayload): Observable<InventorySerial> {
    return this.http.post<InventorySerial>(`${this.apiBase}/api/v1/inventory/serials`, payload);
  }

  updateInventorySerialStatus(id: string, status: string, issuedReferenceNumber?: string): Observable<InventorySerial> {
    return this.http.post<InventorySerial>(`${this.apiBase}/api/v1/inventory/serials/${id}/status`, {
      status,
      issuedReferenceNumber: issuedReferenceNumber ?? null
    });
  }

  getInventoryTraceability(query: { lotNumber?: string; serialNumber?: string; offeringId?: string }): Observable<InventoryTraceabilityResult> {
    const params = new URLSearchParams();
    if (query.lotNumber) params.set('lotNumber', query.lotNumber);
    if (query.serialNumber) params.set('serialNumber', query.serialNumber);
    if (query.offeringId) params.set('offeringId', query.offeringId);
    return this.http.get<InventoryTraceabilityResult>(`${this.apiBase}/api/v1/inventory/traceability?${params.toString()}`);
  }

  listQcRules(): Observable<InventoryQcRule[]> {
    return this.http.get<InventoryQcRule[]>(`${this.apiBase}/api/v1/inventory/qc/rules`);
  }

  listQcInspections(status?: string, inspectionType?: string): Observable<InventoryQcInspection[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (inspectionType) params.set('inspectionType', inspectionType);
    const query = params.toString();
    return this.http.get<InventoryQcInspection[]>(`${this.apiBase}/api/v1/inventory/qc/inspections${query ? `?${query}` : ''}`);
  }

  getQcInspection(id: string): Observable<InventoryQcInspection> {
    return this.http.get<InventoryQcInspection>(`${this.apiBase}/api/v1/inventory/qc/inspections/${id}`);
  }

  createQcInspection(payload: CreateQcInspectionPayload): Observable<InventoryQcInspection> {
    return this.http.post<InventoryQcInspection>(`${this.apiBase}/api/v1/inventory/qc/inspections`, payload);
  }

  completeQcInspection(id: string, payload: CompleteQcInspectionPayload): Observable<InventoryQcInspection> {
    return this.http.post<InventoryQcInspection>(`${this.apiBase}/api/v1/inventory/qc/inspections/${id}/complete`, payload);
  }

  listRecallCampaigns(status?: string): Observable<InventoryRecallCampaign[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<InventoryRecallCampaign[]>(`${this.apiBase}/api/v1/inventory/recalls${query ? `?${query}` : ''}`);
  }

  getRecallCampaign(id: string): Observable<InventoryRecallCampaign> {
    return this.http.get<InventoryRecallCampaign>(`${this.apiBase}/api/v1/inventory/recalls/${id}`);
  }

  createRecallCampaign(payload: CreateRecallCampaignPayload): Observable<InventoryRecallCampaign> {
    return this.http.post<InventoryRecallCampaign>(`${this.apiBase}/api/v1/inventory/recalls`, payload);
  }

  executeRecallCampaign(id: string, batchSize?: number): Observable<ExecuteRecallCampaignResult> {
    return this.http.post<ExecuteRecallCampaignResult>(`${this.apiBase}/api/v1/inventory/recalls/${id}/execute`, {
      batchSize: batchSize ?? null
    });
  }

  simulateRecallCampaign(id: string): Observable<SimulateRecallCampaignResult> {
    return this.http.post<SimulateRecallCampaignResult>(`${this.apiBase}/api/v1/inventory/recalls/${id}/simulate`, {});
  }

  getRecallImpact(id: string): Observable<RecallImpactResult> {
    return this.http.get<RecallImpactResult>(`${this.apiBase}/api/v1/inventory/recalls/${id}/impact`);
  }

  submitRecallForApproval(id: string): Observable<InventoryRecallCampaign> {
    return this.http.post<InventoryRecallCampaign>(`${this.apiBase}/api/v1/inventory/recalls/${id}/submit-for-approval`, {});
  }

  approveRecallCampaign(id: string): Observable<InventoryRecallCampaign> {
    return this.http.post<InventoryRecallCampaign>(`${this.apiBase}/api/v1/inventory/recalls/${id}/approve`, {});
  }

  cancelRecallCampaign(id: string): Observable<InventoryRecallCampaign> {
    return this.http.post<InventoryRecallCampaign>(`${this.apiBase}/api/v1/inventory/recalls/${id}/cancel`, {});
  }

  getRecallControlRoom(): Observable<RecallControlRoomSummary> {
    return this.http.get<RecallControlRoomSummary>(`${this.apiBase}/api/v1/inventory/recalls/control-room`);
  }

  getRecallAuditTrail(id: string): Observable<InventoryRecallAuditEvent[]> {
    return this.http.get<InventoryRecallAuditEvent[]>(`${this.apiBase}/api/v1/inventory/recalls/${id}/audit`);
  }

  enqueueRecallExecution(id: string, batchSize: number, idempotencyKey: string): Observable<InventoryRecallExecutionJob> {
    return this.http.post<InventoryRecallExecutionJob>(`${this.apiBase}/api/v1/inventory/recalls/${id}/enqueue-execution`, {
      batchSize,
      idempotencyKey
    });
  }

  triggerRecallFromQcInspection(id: string): Observable<InventoryRecallCampaign> {
    return this.http.post<InventoryRecallCampaign>(`${this.apiBase}/api/v1/inventory/qc/inspections/${id}/trigger-recall`, {});
  }

  listServiceOrders(status?: string): Observable<ServiceOrder[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<ServiceOrder[]>(`${this.apiBase}/api/v1/services/orders${query ? `?${query}` : ''}`);
  }

  getServiceOrder(id: string): Observable<ServiceOrder> {
    return this.http.get<ServiceOrder>(`${this.apiBase}/api/v1/services/orders/${id}`);
  }

  createServiceOrder(payload: CreateServiceOrderPayload): Observable<ServiceOrder> {
    return this.http.post<ServiceOrder>(`${this.apiBase}/api/v1/services/orders`, payload);
  }

  scheduleServiceOrder(id: string, scheduledStartUtc: string, scheduledEndUtc: string): Observable<ServiceOrder> {
    return this.http.post<ServiceOrder>(`${this.apiBase}/api/v1/services/orders/${id}/schedule`, {
      scheduledStartUtc,
      scheduledEndUtc
    });
  }

  assignServiceOrder(id: string, resourceId: string, isPrimary: boolean): Observable<ServiceOrder> {
    return this.http.post<ServiceOrder>(`${this.apiBase}/api/v1/services/orders/${id}/assign`, { resourceId, isPrimary });
  }

  startServiceOrder(id: string): Observable<ServiceOrder> {
    return this.http.post<ServiceOrder>(`${this.apiBase}/api/v1/services/orders/${id}/start`, {});
  }

  consumeServiceParts(id: string, payload: ConsumeServicePartsPayload): Observable<ServiceConsumption> {
    return this.http.post<ServiceConsumption>(`${this.apiBase}/api/v1/services/orders/${id}/consume-parts`, payload);
  }

  completeServiceOrder(id: string, payload: CompleteServiceOrderPayload): Observable<ServiceOrder> {
    return this.http.post<ServiceOrder>(`${this.apiBase}/api/v1/services/orders/${id}/complete`, payload);
  }

  customerApproveServiceOrder(id: string): Observable<ServiceOrder> {
    return this.http.post<ServiceOrder>(`${this.apiBase}/api/v1/services/orders/${id}/customer-approve`, {});
  }

  listServiceResources(): Observable<ServiceResource[]> {
    return this.http.get<ServiceResource[]>(`${this.apiBase}/api/v1/services/resources`);
  }

  createServiceResource(payload: CreateServiceResourcePayload): Observable<ServiceResource> {
    return this.http.post<ServiceResource>(`${this.apiBase}/api/v1/services/resources`, payload);
  }

  getWarrantyEntitlement(serialOrOrderRef: string): Observable<WarrantyEntitlement> {
    return this.http.get<WarrantyEntitlement>(`${this.apiBase}/api/v1/services/warranty/${encodeURIComponent(serialOrOrderRef)}`);
  }

  listWarrantyContracts(): Observable<WarrantyContract[]> {
    return this.http.get<WarrantyContract[]>(`${this.apiBase}/api/v1/services/warranty/contracts`);
  }

  createWarrantyContract(payload: CreateWarrantyContractPayload): Observable<WarrantyContract> {
    return this.http.post<WarrantyContract>(`${this.apiBase}/api/v1/services/warranty/contracts`, payload);
  }

  listRmaRequests(status?: string): Observable<RmaRequest[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<RmaRequest[]>(`${this.apiBase}/api/v1/services/rma${query ? `?${query}` : ''}`);
  }

  getRmaRequest(id: string): Observable<RmaRequest> {
    return this.http.get<RmaRequest>(`${this.apiBase}/api/v1/services/rma/${id}`);
  }

  createRmaRequest(payload: CreateRmaRequestPayload): Observable<RmaRequest> {
    return this.http.post<RmaRequest>(`${this.apiBase}/api/v1/services/rma`, payload);
  }

  diagnoseRmaRequest(id: string, payload: DiagnoseRmaPayload): Observable<RmaRequest> {
    return this.http.post<RmaRequest>(`${this.apiBase}/api/v1/services/rma/${id}/diagnose`, payload);
  }

  resolveRmaRequest(id: string, payload: ResolveRmaPayload): Observable<RmaRequest> {
    return this.http.post<RmaRequest>(`${this.apiBase}/api/v1/services/rma/${id}/resolve`, payload);
  }

  approveRmaRequest(id: string): Observable<RmaRequest> {
    return this.http.post<RmaRequest>(`${this.apiBase}/api/v1/services/rma/${id}/approve`, {});
  }

  getOperationsControlRoom(): Observable<OperationsControlRoomSummary> {
    return this.http.get<OperationsControlRoomSummary>(`${this.apiBase}/api/v1/operations/control-room`);
  }

  getTrackingDashboard(): Observable<TrackingDashboardSummary> {
    return this.http.get<TrackingDashboardSummary>(`${this.apiBase}/api/v1/inventory/tracking/dashboard`);
  }

  getDispatchBoard(fromUtc?: string, toUtc?: string): Observable<DispatchBoardEntry[]> {
    const params = new URLSearchParams();
    if (fromUtc) params.set('fromUtc', fromUtc);
    if (toUtc) params.set('toUtc', toUtc);
    const query = params.toString();
    return this.http.get<DispatchBoardEntry[]>(`${this.apiBase}/api/v1/services/dispatch-board${query ? `?${query}` : ''}`);
  }

  getTechnicianWorkQueue(resourceId: string): Observable<TechnicianWorkQueueEntry[]> {
    return this.http.get<TechnicianWorkQueueEntry[]>(`${this.apiBase}/api/v1/services/technician/work-queue?resourceId=${resourceId}`);
  }

  reassignDispatchBoard(orderId: string, resourceId: string, isPrimary: boolean): Observable<{ orderId: string; resourceId: string }> {
    return this.http.post<{ orderId: string; resourceId: string }>(`${this.apiBase}/api/v1/services/dispatch-board/reassign`, {
      orderId,
      resourceId,
      isPrimary
    });
  }

  listServiceCompletionEvidence(orderId: string): Observable<ServiceCompletionEvidence[]> {
    return this.http.get<ServiceCompletionEvidence[]>(`${this.apiBase}/api/v1/services/orders/${orderId}/evidence`);
  }

  addServiceCompletionEvidence(orderId: string, payload: AddServiceCompletionEvidencePayload): Observable<ServiceCompletionEvidence> {
    return this.http.post<ServiceCompletionEvidence>(`${this.apiBase}/api/v1/services/orders/${orderId}/evidence`, payload);
  }

  listServiceSlaEscalations(status?: string): Observable<ServiceSlaEscalation[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<ServiceSlaEscalation[]>(`${this.apiBase}/api/v1/services/sla-escalations${query ? `?${query}` : ''}`);
  }

  acknowledgeServiceSlaEscalation(id: string): Observable<ServiceSlaEscalation> {
    return this.http.post<ServiceSlaEscalation>(`${this.apiBase}/api/v1/services/sla-escalations/${id}/acknowledge`, {});
  }

  getOperationsPostingProfile(): Observable<CatalogOperationsPostingProfile> {
    return this.http.get<CatalogOperationsPostingProfile>(`${this.apiBase}/api/v1/catalog/operations/posting-profile`);
  }

  upsertOperationsPostingProfile(payload: UpsertCatalogOperationsPostingProfilePayload): Observable<CatalogOperationsPostingProfile> {
    return this.http.put<CatalogOperationsPostingProfile>(`${this.apiBase}/api/v1/catalog/operations/posting-profile`, payload);
  }

  bootstrapPhase3PostingProfile(currencyCode?: string): Observable<BootstrapPhase3PostingResult> {
    return this.http.post<BootstrapPhase3PostingResult>(
      `${this.apiBase}/api/v1/catalog/operations/posting-profile/bootstrap-phase3`,
      { currencyCode: currencyCode ?? null }
    );
  }

  bootstrapPhase4PostingProfile(currencyCode?: string): Observable<BootstrapPhase4PostingResult> {
    return this.http.post<BootstrapPhase4PostingResult>(
      `${this.apiBase}/api/v1/catalog/operations/posting-profile/bootstrap-phase4`,
      { currencyCode: currencyCode ?? null }
    );
  }

  bootstrapPhase5PostingProfile(currencyCode?: string): Observable<BootstrapPhase5PostingResult> {
    return this.http.post<BootstrapPhase5PostingResult>(
      `${this.apiBase}/api/v1/catalog/operations/posting-profile/bootstrap-phase5`,
      { currencyCode: currencyCode ?? null }
    );
  }

  listDigitalEntitlements(): Observable<DigitalEntitlement[]> {
    return this.http.get<DigitalEntitlement[]>(`${this.apiBase}/api/v1/digital/entitlements`);
  }

  getDigitalEntitlementDetail(id: string): Observable<DigitalEntitlementDetail> {
    return this.http.get<DigitalEntitlementDetail>(`${this.apiBase}/api/v1/digital/entitlements/${id}`);
  }

  generateDigitalLicense(entitlementId: string): Observable<DigitalLicense> {
    return this.http.post<DigitalLicense>(
      `${this.apiBase}/api/v1/digital/licenses/generate`,
      { entitlementId });
  }

  revokeDigitalEntitlement(id: string, reason?: string): Observable<DigitalEntitlement> {
    return this.http.post<DigitalEntitlement>(
      `${this.apiBase}/api/v1/digital/entitlements/${id}/revoke`,
      { reason: reason ?? null });
  }

  provisionDigitalEntitlement(payload: {
    offeringId: string;
    customerId: string;
    customerReference?: string;
    saleAmount: number;
    currencyCode?: string;
    usageCreditsGranted: number;
    postFinancialEntry?: boolean;
    accessEndsAtUtc?: string | null;
  }): Observable<DigitalEntitlement & { downloadToken?: string | null }> {
    return this.http.post<DigitalEntitlement & { downloadToken?: string | null }>(
      `${this.apiBase}/api/v1/digital/entitlements/provision`,
      {
        offeringId: payload.offeringId,
        customerId: payload.customerId,
        customerReference: payload.customerReference ?? null,
        saleAmount: payload.saleAmount,
        currencyCode: payload.currencyCode ?? null,
        usageCreditsGranted: payload.usageCreditsGranted,
        accessStartsAtUtc: new Date().toISOString(),
        accessEndsAtUtc: payload.accessEndsAtUtc ?? null,
        postFinancialEntry: payload.postFinancialEntry ?? false
      });
  }

  getRegulatedFinanceDashboard(): Observable<RegulatedFinanceDashboard> {
    return this.http.get<RegulatedFinanceDashboard>(`${this.apiBase}/api/v1/reg-finance/dashboard`);
  }

  createBrokerageAccount(payload: {
    accountNumber: string;
    customerId: string;
    suitabilityApproved: boolean;
  }): Observable<BrokerageAccount> {
    return this.http.post<BrokerageAccount>(`${this.apiBase}/api/v1/reg-finance/brokerage/accounts`, payload);
  }

  listBrokerageAccounts(): Observable<BrokerageAccount[]> {
    return this.http.get<BrokerageAccount[]>(`${this.apiBase}/api/v1/reg-finance/brokerage/accounts`);
  }

  submitPreciousMetalOrder(payload: { quoteId?: string | null; side: string; quantityGrams: number; limitPrice: number }): Observable<PreciousMetalOrder> {
    return this.http.post<PreciousMetalOrder>(`${this.apiBase}/api/v1/reg-finance/precious-metals/orders`, payload);
  }

  settlePreciousMetalOrder(id: string, settledAmount: number, currencyCode = 'USD'): Observable<PreciousMetalOrder> {
    return this.http.post<PreciousMetalOrder>(
      `${this.apiBase}/api/v1/reg-finance/precious-metals/orders/${id}/settle`,
      { settledAmount, currencyCode });
  }

  submitBrokerageOrder(payload: {
    accountId: string;
    symbol: string;
    side: string;
    quantity: number;
  }): Observable<BrokerageOrder> {
    return this.http.post<BrokerageOrder>(`${this.apiBase}/api/v1/reg-finance/brokerage/orders`, payload);
  }

  executeBrokerageOrder(id: string): Observable<{ id: string; orderId: string }> {
    return this.http.post<{ id: string; orderId: string }>(
      `${this.apiBase}/api/v1/reg-finance/brokerage/orders/${id}/execute`,
      null);
  }

  issueInsurancePolicy(payload: {
    policyNumber: string;
    customerId: string;
    premiumAmount: number;
    currencyCode?: string;
    effectiveFromUtc: string;
    effectiveToUtc: string;
  }): Observable<InsurancePolicy> {
    return this.http.post<InsurancePolicy>(`${this.apiBase}/api/v1/reg-finance/insurance/policies`, payload);
  }

  executeFxTrade(payload: {
    baseCurrency: string;
    quoteCurrency: string;
    baseAmount: number;
    rate: number;
  }): Observable<FxTrade> {
    return this.http.post<FxTrade>(`${this.apiBase}/api/v1/reg-finance/fx/trades`, {
      quoteId: null,
      ...payload
    });
  }

  runComplianceScreen(payload: { subjectType: string; subjectId: string; screeningType: string; simulateHardStop?: boolean }): Observable<ComplianceScreenResult> {
    return this.http.post<ComplianceScreenResult>(`${this.apiBase}/api/v1/reg-finance/compliance/screen`, payload);
  }

  listUnifiedCommerceOrders(): Observable<UnifiedCommerceOrder[]> {
    return this.http.get<UnifiedCommerceOrder[]>(`${this.apiBase}/api/v1/commerce/orders`);
  }

  createUnifiedCommerceOrder(payload: {
    offeringId: string;
    customerId: string;
    customerReference?: string;
    grossAmount: number;
    currencyCode?: string;
    idempotencyKey: string;
    marketplaceSellerId?: string | null;
  }): Observable<UnifiedCommerceOrder> {
    return this.http.post<UnifiedCommerceOrder>(`${this.apiBase}/api/v1/commerce/orders`, {
      offeringId: payload.offeringId,
      customerId: payload.customerId,
      customerReference: payload.customerReference ?? null,
      grossAmount: payload.grossAmount,
      currencyCode: payload.currencyCode ?? null,
      idempotencyKey: payload.idempotencyKey,
      marketplaceSellerId: payload.marketplaceSellerId ?? null
    });
  }

  createProductSubscriptionContract(payload: {
    planId: string;
    customerId: string;
    customerReference?: string;
    periodDays?: number;
    billImmediately?: boolean;
  }): Observable<ProductSubscriptionContract> {
    return this.http.post<ProductSubscriptionContract>(
      `${this.apiBase}/api/v1/subscription-products/contracts`,
      {
        planId: payload.planId,
        customerId: payload.customerId,
        customerReference: payload.customerReference ?? null,
        periodDays: payload.periodDays ?? 30,
        billImmediately: payload.billImmediately ?? false
      });
  }

  checkoutUnifiedCommerceOrder(id: string, postFinancialEntry = true, usageCreditsGranted = 100): Observable<UnifiedCommerceOrder> {
    return this.http.post<UnifiedCommerceOrder>(
      `${this.apiBase}/api/v1/commerce/orders/${id}/checkout`,
      { postFinancialEntry, usageCreditsGranted });
  }

  listProductSubscriptionPlans(): Observable<ProductSubscriptionPlan[]> {
    return this.http.get<ProductSubscriptionPlan[]>(`${this.apiBase}/api/v1/subscription-products/plans`);
  }

  listProductSubscriptionContracts(status?: string): Observable<ProductSubscriptionContract[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<ProductSubscriptionContract[]>(
      `${this.apiBase}/api/v1/subscription-products/contracts${query ? `?${query}` : ''}`);
  }

  getProductSubscriptionContractDetail(id: string): Observable<ProductSubscriptionContractDetail> {
    return this.http.get<ProductSubscriptionContractDetail>(
      `${this.apiBase}/api/v1/subscription-products/contracts/${id}/detail`);
  }

  runProductSubscriptionDueBilling(postFinancialEntry = true): Observable<RunProductSubscriptionBillingResult> {
    return this.http.post<RunProductSubscriptionBillingResult>(
      `${this.apiBase}/api/v1/subscription-products/billing/run-due`,
      { postFinancialEntry });
  }

  processProductSubscriptionLifecycle(postFinancialEntry = true): Observable<ProcessProductSubscriptionLifecycleResult> {
    return this.http.post<ProcessProductSubscriptionLifecycleResult>(
      `${this.apiBase}/api/v1/subscription-products/billing/process-lifecycle`,
      { postFinancialEntry });
  }

  issueProductSubscriptionInvoice(scheduleId: string, postFinancialEntry = true): Observable<ProductSubscriptionInvoice> {
    return this.http.post<ProductSubscriptionInvoice>(
      `${this.apiBase}/api/v1/subscription-products/invoices/${scheduleId}/issue`,
      { postFinancialEntry });
  }

  recognizeProductSubscriptionRevenue(scheduleId: string, postFinancialEntry = true): Observable<ProductSubscriptionInvoice> {
    return this.http.post<ProductSubscriptionInvoice>(
      `${this.apiBase}/api/v1/subscription-products/invoices/${scheduleId}/recognize`,
      { postFinancialEntry });
  }

  listMarketplaceSellers(): Observable<MarketplaceSeller[]> {
    return this.http.get<MarketplaceSeller[]>(`${this.apiBase}/api/v1/marketplace/sellers`);
  }

  listMarketplaceOfferingProposals(status?: string): Observable<MarketplaceOfferingProposal[]> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.http.get<MarketplaceOfferingProposal[]>(`${this.apiBase}/api/v1/marketplace/offering-proposals${query}`);
  }

  getMarketplaceOfferingProposal(id: string): Observable<MarketplaceOfferingProposal> {
    return this.http.get<MarketplaceOfferingProposal>(`${this.apiBase}/api/v1/marketplace/offering-proposals/${id}`);
  }

  beginReviewMarketplaceOfferingProposal(id: string, payload: { actorReference?: string; notes?: string }): Observable<MarketplaceOfferingProposal> {
    return this.http.post<MarketplaceOfferingProposal>(`${this.apiBase}/api/v1/marketplace/offering-proposals/${id}/review`, payload);
  }

  approveMarketplaceOfferingProposal(id: string, payload: { actorReference?: string; notes?: string }): Observable<MarketplaceOfferingProposal> {
    return this.http.post<MarketplaceOfferingProposal>(`${this.apiBase}/api/v1/marketplace/offering-proposals/${id}/approve`, payload);
  }

  rejectMarketplaceOfferingProposal(id: string, payload: { actorReference?: string; reason: string }): Observable<MarketplaceOfferingProposal> {
    return this.http.post<MarketplaceOfferingProposal>(`${this.apiBase}/api/v1/marketplace/offering-proposals/${id}/reject`, payload);
  }

  createMarketplaceSeller(payload: {
    sellerCode: string;
    displayName: string;
    partyId?: string | null;
    customerAccountId?: string | null;
    kycReference?: string | null;
    complianceEvidenceReference?: string | null;
  }): Observable<MarketplaceSeller> {
    return this.http.post<MarketplaceSeller>(`${this.apiBase}/api/v1/marketplace/sellers`, {
      sellerCode: payload.sellerCode,
      displayName: payload.displayName,
      partyId: payload.partyId ?? null,
      customerAccountId: payload.customerAccountId ?? null,
      kycReference: payload.kycReference ?? null,
      complianceEvidenceReference: payload.complianceEvidenceReference ?? null
    });
  }

  getMarketplaceSeller(id: string): Observable<MarketplaceSeller> {
    return this.http.get<MarketplaceSeller>(`${this.apiBase}/api/v1/marketplace/sellers/${id}`);
  }

  updateMarketplaceSeller(id: string, payload: {
    displayName: string;
    publicProfileSummary?: string | null;
    returnPolicySummary?: string | null;
    slaPolicySummary?: string | null;
    partyId?: string | null;
    customerAccountId?: string | null;
    kycReference?: string | null;
    complianceEvidenceReference?: string | null;
    payoutProfileReference?: string | null;
  }): Observable<MarketplaceSeller> {
    return this.http.put<MarketplaceSeller>(`${this.apiBase}/api/v1/marketplace/sellers/${id}`, payload);
  }

  submitMarketplaceSeller(id: string, payload: { actorReference?: string; reason?: string }): Observable<MarketplaceSeller> {
    return this.http.post<MarketplaceSeller>(`${this.apiBase}/api/v1/marketplace/sellers/${id}/submit`, payload);
  }

  rejectMarketplaceSeller(id: string, payload: { actorReference?: string; reason: string }): Observable<MarketplaceSeller> {
    return this.http.post<MarketplaceSeller>(`${this.apiBase}/api/v1/marketplace/sellers/${id}/reject`, payload);
  }

  suspendMarketplaceSeller(id: string, payload: { actorReference?: string; reason: string }): Observable<MarketplaceSeller> {
    return this.http.post<MarketplaceSeller>(`${this.apiBase}/api/v1/marketplace/sellers/${id}/suspend`, payload);
  }

  reactivateMarketplaceSeller(id: string, payload: { actorReference?: string; reason?: string }): Observable<MarketplaceSeller> {
    return this.http.post<MarketplaceSeller>(`${this.apiBase}/api/v1/marketplace/sellers/${id}/reactivate`, payload);
  }

  assignMarketplaceSellerCommissionPolicy(id: string, commissionPolicyId: string | null): Observable<MarketplaceSeller> {
    return this.http.put<MarketplaceSeller>(`${this.apiBase}/api/v1/marketplace/sellers/${id}/commission-policy`, {
      commissionPolicyId
    });
  }

  approveMarketplaceSeller(id: string, payload?: { actorReference?: string; reason?: string }): Observable<MarketplaceSeller> {
    return this.http.post<MarketplaceSeller>(`${this.apiBase}/api/v1/marketplace/sellers/${id}/approve`, payload ?? {});
  }

  listMarketplaceSellerOrders(sellerId: string, page = 1, pageSize = 50): Observable<MarketplaceSellerOrderList> {
    return this.http.get<MarketplaceSellerOrderList>(
      `${this.apiBase}/api/v1/marketplace/sellers/${sellerId}/orders?page=${page}&pageSize=${pageSize}`);
  }

  listMarketplaceSellerSettlements(sellerId: string): Observable<MarketplaceSettlementSummary[]> {
    return this.http.get<MarketplaceSettlementSummary[]>(`${this.apiBase}/api/v1/marketplace/sellers/${sellerId}/settlements`);
  }

  listMarketplaceSellerDisputes(sellerId: string, status?: string): Observable<MarketplaceDispute[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<MarketplaceDispute[]>(
      `${this.apiBase}/api/v1/marketplace/sellers/${sellerId}/disputes${query ? `?${query}` : ''}`);
  }

  listMarketplaceSellerDocuments(sellerId: string): Observable<MarketplaceSellerApplicationDocument[]> {
    return this.http.get<MarketplaceSellerApplicationDocument[]>(
      `${this.apiBase}/api/v1/marketplace/sellers/${sellerId}/documents`);
  }

  catalogFileContentUrl(fileId: string): string {
    return `${this.apiBase}/api/v1/catalog/files/${fileId}/content`;
  }

  marketplaceSellerDocumentContentUrl(sellerId: string, documentId: string): string {
    return `${this.apiBase}/api/v1/marketplace/sellers/${sellerId}/documents/${documentId}/content`;
  }

  verifyMarketplaceSellerDocument(sellerId: string, documentId: string, payload?: { reviewNotes?: string; actorReference?: string }): Observable<MarketplaceSellerApplicationDocument> {
    return this.http.post<MarketplaceSellerApplicationDocument>(
      `${this.apiBase}/api/v1/marketplace/sellers/${sellerId}/documents/${documentId}/verify`,
      payload ?? {});
  }

  rejectMarketplaceSellerDocument(sellerId: string, documentId: string, payload: { reason: string; actorReference?: string }): Observable<MarketplaceSellerApplicationDocument> {
    return this.http.post<MarketplaceSellerApplicationDocument>(
      `${this.apiBase}/api/v1/marketplace/sellers/${sellerId}/documents/${documentId}/reject`,
      payload);
  }

  createMarketplaceOffer(payload: {
    sellerId: string;
    offeringId: string;
    price: number;
    currencyCode?: string;
    fulfillmentSlaHours: number;
    storeId?: string | null;
    activate?: boolean;
  }): Observable<MarketplaceSellerOfferSummary> {
    return this.http.post<MarketplaceSellerOfferSummary>(`${this.apiBase}/api/v1/marketplace/offers`, payload);
  }

  updateMarketplaceOffer(id: string, payload: {
    price: number;
    currencyCode?: string;
    fulfillmentSlaHours: number;
    storeId?: string | null;
  }): Observable<MarketplaceSellerOfferSummary> {
    return this.http.put<MarketplaceSellerOfferSummary>(`${this.apiBase}/api/v1/marketplace/offers/${id}`, payload);
  }

  activateMarketplaceOffer(id: string): Observable<MarketplaceSellerOfferSummary> {
    return this.http.post<MarketplaceSellerOfferSummary>(`${this.apiBase}/api/v1/marketplace/offers/${id}/activate`, null);
  }

  deactivateMarketplaceOffer(id: string): Observable<MarketplaceSellerOfferSummary> {
    return this.http.post<MarketplaceSellerOfferSummary>(`${this.apiBase}/api/v1/marketplace/offers/${id}/deactivate`, null);
  }

  holdMarketplaceEscrow(
    orderReferenceId: string,
    amount: number,
    currencyCode = 'USD'
  ): Observable<{ id: string; orderReferenceId: string; amount: number; currencyCode: string; status: string }> {
    return this.http.post<{ id: string; orderReferenceId: string; amount: number; currencyCode: string; status: string }>(
      `${this.apiBase}/api/v1/marketplace/orders/${orderReferenceId}/escrow/hold`,
      { amount, currencyCode });
  }

  settleMarketplaceOrder(
    orderReferenceId: string,
    payload: {
      sellerId: string;
      grossAmount: number;
      commissionAmount: number;
      currencyCode?: string;
      idempotencyKey: string;
      postFinancialEntry?: boolean;
      requireEscrowRelease?: boolean;
    }
  ): Observable<MarketplaceSettlementSummary> {
    return this.http.post<MarketplaceSettlementSummary>(
      `${this.apiBase}/api/v1/marketplace/orders/${orderReferenceId}/settle`,
      {
        sellerId: payload.sellerId,
        grossAmount: payload.grossAmount,
        commissionAmount: payload.commissionAmount,
        currencyCode: payload.currencyCode ?? 'USD',
        idempotencyKey: payload.idempotencyKey,
        postFinancialEntry: payload.postFinancialEntry ?? false,
        requireEscrowRelease: payload.requireEscrowRelease ?? true
      });
  }

  openMarketplaceDispute(payload: {
    orderReferenceId: string;
    sellerId: string;
    reason: string;
    disputedAmount: number;
  }): Observable<MarketplaceDispute> {
    return this.http.post<MarketplaceDispute>(`${this.apiBase}/api/v1/marketplace/disputes`, {
      orderReferenceId: payload.orderReferenceId,
      sellerId: payload.sellerId,
      reason: payload.reason,
      disputedAmount: payload.disputedAmount
    });
  }

  listMarketplaceCommissionRules(): Observable<MarketplaceCommissionRuleRecord[]> {
    return this.http.get<MarketplaceCommissionRuleRecord[]>(`${this.apiBase}/api/v1/marketplace/commission-rules`);
  }

  createMarketplaceCommissionRule(payload: CreateMarketplaceCommissionRulePayload): Observable<MarketplaceCommissionRuleRecord> {
    return this.http.post<MarketplaceCommissionRuleRecord>(`${this.apiBase}/api/v1/marketplace/commission-rules`, payload);
  }

  simulateMarketplaceCommission(payload: SimulateMarketplaceCommissionPayload): Observable<MarketplaceCommissionSimulationResult> {
    return this.http.post<MarketplaceCommissionSimulationResult>(
      `${this.apiBase}/api/v1/marketplace/commission-rules/simulate`,
      payload);
  }

  migrateMarketplaceCommissionFromPolicies(): Observable<{ rulesCreated: number }> {
    return this.http.post<{ rulesCreated: number }>(
      `${this.apiBase}/api/v1/marketplace/commission-rules/migrate-from-policies`,
      {});
  }

  listMarketplaceSellerTierBands(): Observable<MarketplaceSellerTierBandRecord[]> {
    return this.http.get<MarketplaceSellerTierBandRecord[]>(`${this.apiBase}/api/v1/marketplace/seller-tiers/bands`);
  }

  listMarketplaceSellerTierProfiles(): Observable<MarketplaceSellerTierProfileRecord[]> {
    return this.http.get<MarketplaceSellerTierProfileRecord[]>(`${this.apiBase}/api/v1/marketplace/seller-tiers/profiles`);
  }

  overrideMarketplaceSellerTier(
    sellerId: string,
    payload: MarketplaceSellerTierOverridePayload
  ): Observable<MarketplaceSellerTierProfileRecord> {
    return this.http.put<MarketplaceSellerTierProfileRecord>(
      `${this.apiBase}/api/v1/marketplace/seller-tiers/profiles/${sellerId}/override`,
      payload);
  }

  runMarketplaceSellerTierScoring(): Observable<{ sellersScored: number }> {
    return this.http.post<{ sellersScored: number }>(`${this.apiBase}/api/v1/marketplace/seller-tiers/score-run`, {});
  }

  listRetailMediaCampaigns(sellerId?: string): Observable<RetailMediaCampaignRecord[]> {
    const params = sellerId ? `?sellerId=${sellerId}` : '';
    return this.http.get<RetailMediaCampaignRecord[]>(`${this.apiBase}/api/v1/marketplace/retail-media/campaigns${params}`);
  }

  approveRetailMediaCampaign(campaignId: string): Observable<RetailMediaCampaignRecord> {
    return this.http.post<RetailMediaCampaignRecord>(
      `${this.apiBase}/api/v1/marketplace/retail-media/campaigns/${campaignId}/approve`,
      {});
  }

  getMarketplaceDashboard(): Observable<MarketplaceDashboard> {
    return this.http.get<MarketplaceDashboard>(`${this.apiBase}/api/v1/marketplace/dashboard`);
  }

  getMarketplaceLaunchReadiness(): Observable<MarketplaceLaunchReadinessSummary> {
    return this.http.get<MarketplaceLaunchReadinessSummary>(`${this.apiBase}/api/v1/marketplace/launch-readiness`);
  }

  listMarketplaceDisputes(status?: string): Observable<MarketplaceDispute[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<MarketplaceDispute[]>(
      `${this.apiBase}/api/v1/marketplace/disputes${query ? `?${query}` : ''}`);
  }

  reviewMarketplaceDispute(id: string): Observable<MarketplaceDispute> {
    return this.http.post<MarketplaceDispute>(`${this.apiBase}/api/v1/marketplace/disputes/${id}/review`, {});
  }

  resolveMarketplaceDispute(
    id: string,
    payload: {
      favorBuyer: boolean;
      firstApproverReference: string;
      secondApproverReference?: string | null;
      resolutionNotes?: string;
      postFinancialAdjustment?: boolean;
    }
  ): Observable<MarketplaceDispute> {
    return this.http.post<MarketplaceDispute>(`${this.apiBase}/api/v1/marketplace/disputes/${id}/resolve`, payload);
  }

  listMarketplaceStores(): Observable<MarketplaceStore[]> {
    return this.http.get<MarketplaceStore[]>(`${this.apiBase}/api/v1/marketplace/stores`);
  }

  getMarketplaceStore(id: string): Observable<MarketplaceStore> {
    return this.http.get<MarketplaceStore>(`${this.apiBase}/api/v1/marketplace/stores/${id}`);
  }

  createMarketplaceStore(payload: UpsertMarketplaceStorePayload): Observable<MarketplaceStore> {
    return this.http.post<MarketplaceStore>(`${this.apiBase}/api/v1/marketplace/stores`, payload);
  }

  updateMarketplaceStore(id: string, payload: UpsertMarketplaceStorePayload): Observable<MarketplaceStore> {
    return this.http.put<MarketplaceStore>(`${this.apiBase}/api/v1/marketplace/stores/${id}`, payload);
  }

  getStoreBranding(storeId: string): Observable<StoreBranding> {
    return this.http.get<StoreBranding>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/branding`);
  }

  updateStoreBranding(storeId: string, payload: UpsertStoreBrandingPayload): Observable<StoreBranding> {
    return this.http.put<StoreBranding>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/branding`, payload);
  }

  uploadStoreBrandingAsset(storeId: string, file: File): Observable<StoreBrandingUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<StoreBrandingUploadResult>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/branding/upload`, formData);
  }

  brandingFilePreviewUrl(storeId: string, fileId: string | null | undefined): string | null {
    if (!fileId) {
      return null;
    }
    return `${this.apiBase}/api/v1/marketplace/stores/${storeId}/branding/files/${fileId}/content`;
  }

  getStoreProfile(storeId: string): Observable<StorefrontProfileResponse> {
    return this.http.get<StorefrontProfileResponse>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/profile`);
  }

  updateStoreProfile(storeId: string, profile: StorefrontContentProfile): Observable<StorefrontProfileResponse> {
    return this.http.put<StorefrontProfileResponse>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/profile`, { profile });
  }

  generateStoreProfileDraft(storeId: string, payload: GenerateStorefrontProfileDraftPayload): Observable<GenerateStorefrontProfileDraftResponse> {
    return this.http.post<GenerateStorefrontProfileDraftResponse>(
      `${this.apiBase}/api/v1/marketplace/stores/${storeId}/profile/generate-draft`,
      payload
    );
  }

  composeStoreGrowthPrompt(
    storeId: string,
    locale: string | null | undefined,
    currentProfile: StorefrontContentProfile
  ): Observable<ComposeStorefrontGrowthPromptResponse> {
    return this.http.post<ComposeStorefrontGrowthPromptResponse>(
      `${this.apiBase}/api/v1/marketplace/stores/${storeId}/profile/compose-growth-prompt`,
      { locale: locale ?? null, currentProfile }
    );
  }

  getTenantDefaultProfile(): Observable<StorefrontProfileResponse> {
    return this.http.get<StorefrontProfileResponse>(`${this.apiBase}/api/v1/marketplace/profile/tenant-default`);
  }

  updateTenantDefaultProfile(profile: StorefrontContentProfile): Observable<StorefrontProfileResponse> {
    return this.http.put<StorefrontProfileResponse>(`${this.apiBase}/api/v1/marketplace/profile/tenant-default`, { profile });
  }

  listStoreProfileVersions(storeId: string, locale?: string | null): Observable<StorefrontProfileVersionSummary[]> {
    const params = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return this.http.get<StorefrontProfileVersionSummary[]>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/profile/versions${params}`);
  }

  getStoreProfileDraft(storeId: string, locale?: string | null): Observable<StorefrontProfileDraftResponse> {
    const params: Record<string, string> = { silent: 'true' };
    if (locale) {
      params['locale'] = locale;
    }
    return this.http.get<StorefrontProfileDraftResponse>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/profile/draft`, { params });
  }

  getStoreProfilePublished(storeId: string, locale?: string | null): Observable<StorefrontProfilePublishedResponse> {
    const params = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return this.http.get<StorefrontProfilePublishedResponse>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/profile/published${params}`);
  }

  saveStoreProfileDraft(storeId: string, payload: SaveStorefrontProfileDraftPayload): Observable<StorefrontProfileDraftResponse> {
    return this.http.put<StorefrontProfileDraftResponse>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/profile/draft`, payload);
  }

  publishStoreProfileDraft(storeId: string, locale?: string | null): Observable<StorefrontProfilePublishedResponse> {
    const params = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return this.http.post<StorefrontProfilePublishedResponse>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/profile/publish${params}`, {});
  }

  rollbackStoreProfile(storeId: string, payload: StorefrontProfileRollbackPayload): Observable<StorefrontProfilePublishedResponse> {
    return this.http.post<StorefrontProfilePublishedResponse>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/profile/rollback`, payload);
  }

  submitStoreProfileApproval(storeId: string, locale?: string | null): Observable<StorefrontProfileDraftResponse> {
    const params = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return this.http.post<StorefrontProfileDraftResponse>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/profile/approvals/submit${params}`, {});
  }

  approveStoreProfileDraft(storeId: string, payload: StorefrontProfileApprovalPayload, locale?: string | null): Observable<StorefrontProfileDraftResponse> {
    const params = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return this.http.post<StorefrontProfileDraftResponse>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/profile/approvals/approve${params}`, payload);
  }

  rejectStoreProfileDraft(storeId: string, payload: StorefrontProfileApprovalPayload, locale?: string | null): Observable<StorefrontProfileDraftResponse> {
    const params = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return this.http.post<StorefrontProfileDraftResponse>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/profile/approvals/reject${params}`, payload);
  }

  listTenantProfileVersions(locale?: string | null): Observable<StorefrontProfileVersionSummary[]> {
    const params = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return this.http.get<StorefrontProfileVersionSummary[]>(`${this.apiBase}/api/v1/marketplace/profile/tenant-default/versions${params}`);
  }

  getTenantProfileDraft(locale?: string | null): Observable<StorefrontProfileDraftResponse> {
    const params: Record<string, string> = { silent: 'true' };
    if (locale) {
      params['locale'] = locale;
    }
    return this.http.get<StorefrontProfileDraftResponse>(`${this.apiBase}/api/v1/marketplace/profile/tenant-default/draft`, { params });
  }

  getTenantProfilePublished(locale?: string | null): Observable<StorefrontProfilePublishedResponse> {
    const params = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return this.http.get<StorefrontProfilePublishedResponse>(`${this.apiBase}/api/v1/marketplace/profile/tenant-default/published${params}`);
  }

  saveTenantProfileDraft(payload: SaveStorefrontProfileDraftPayload): Observable<StorefrontProfileDraftResponse> {
    return this.http.put<StorefrontProfileDraftResponse>(`${this.apiBase}/api/v1/marketplace/profile/tenant-default/draft`, payload);
  }

  publishTenantProfileDraft(locale?: string | null): Observable<StorefrontProfilePublishedResponse> {
    const params = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return this.http.post<StorefrontProfilePublishedResponse>(`${this.apiBase}/api/v1/marketplace/profile/tenant-default/publish${params}`, {});
  }

  rollbackTenantProfile(payload: StorefrontProfileRollbackPayload): Observable<StorefrontProfilePublishedResponse> {
    return this.http.post<StorefrontProfilePublishedResponse>(`${this.apiBase}/api/v1/marketplace/profile/tenant-default/rollback`, payload);
  }

  listStoreDomains(storeId: string): Observable<StoreDomainBinding[]> {
    return this.http.get<StoreDomainBinding[]>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/domains`);
  }

  upsertStoreDomain(storeId: string, payload: UpsertStoreDomainBindingPayload): Observable<StoreDomainBinding> {
    return this.http.post<StoreDomainBinding>(`${this.apiBase}/api/v1/marketplace/stores/${storeId}/domains`, payload);
  }

  listMarketplaceOffers(offeringsId?: string, sellerId?: string): Observable<MarketplaceSellerOfferSummary[]> {
    const params = new URLSearchParams();
    if (offeringsId) params.set('offeringId', offeringsId);
    if (sellerId) params.set('sellerId', sellerId);
    const query = params.toString();
    return this.http.get<MarketplaceSellerOfferSummary[]>(`${this.apiBase}/api/v1/marketplace/offers${query ? `?${query}` : ''}`);
  }

  listStorefrontPublications(storeId?: string, status?: string): Observable<StorefrontPublication[]> {
    const params = new URLSearchParams();
    if (storeId) params.set('storeId', storeId);
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<StorefrontPublication[]>(`${this.apiBase}/api/v1/marketplace/storefront/publications${query ? `?${query}` : ''}`);
  }

  getStorefrontPublication(id: string): Observable<StorefrontPublication> {
    return this.http.get<StorefrontPublication>(`${this.apiBase}/api/v1/marketplace/storefront/publications/${id}`);
  }

  upsertStorefrontPublication(payload: UpsertStorefrontPublicationPayload): Observable<StorefrontPublication> {
    return this.http.post<StorefrontPublication>(`${this.apiBase}/api/v1/marketplace/storefront/publications`, payload);
  }

  publishStorefrontPublication(id: string): Observable<StorefrontPublication> {
    return this.http.post<StorefrontPublication>(`${this.apiBase}/api/v1/marketplace/storefront/publications/${id}/publish`, {});
  }

  unpublishStorefrontPublication(id: string): Observable<StorefrontPublication> {
    return this.http.post<StorefrontPublication>(`${this.apiBase}/api/v1/marketplace/storefront/publications/${id}/unpublish`, {});
  }

  archiveStorefrontPublication(id: string): Observable<StorefrontPublication> {
    return this.http.post<StorefrontPublication>(`${this.apiBase}/api/v1/marketplace/storefront/publications/${id}/archive`, {});
  }

  listStorefrontCategoryPlacements(storeId: string): Observable<StorefrontCategoryPlacement[]> {
    return this.http.get<StorefrontCategoryPlacement[]>(`${this.apiBase}/api/v1/marketplace/storefront/category-placements?storeId=${storeId}`);
  }

  upsertStorefrontCategoryPlacement(payload: UpsertStorefrontCategoryPlacementPayload): Observable<StorefrontCategoryPlacement> {
    return this.http.post<StorefrontCategoryPlacement>(`${this.apiBase}/api/v1/marketplace/storefront/category-placements`, payload);
  }

  reorderStorefrontCategoryPlacements(storeId: string, payload: ReorderCategoryPlacementPayload[]): Observable<StorefrontCategoryPlacement[]> {
    return this.http.post<StorefrontCategoryPlacement[]>(
      `${this.apiBase}/api/v1/marketplace/storefront/category-placements/reorder?storeId=${storeId}`,
      payload);
  }

  listStorefrontSections(storeId: string): Observable<StorefrontSection[]> {
    return this.http.get<StorefrontSection[]>(`${this.apiBase}/api/v1/marketplace/storefront/sections?storeId=${storeId}`);
  }

  upsertStorefrontSection(payload: UpsertStorefrontSectionPayload): Observable<StorefrontSection> {
    return this.http.post<StorefrontSection>(`${this.apiBase}/api/v1/marketplace/storefront/sections`, payload);
  }

  publishStorefrontSection(id: string): Observable<StorefrontSection> {
    return this.http.post<StorefrontSection>(`${this.apiBase}/api/v1/marketplace/storefront/sections/${id}/publish`, {});
  }

  reorderStorefrontSections(storeId: string, payload: ReorderSectionPayload[]): Observable<StorefrontSection[]> {
    return this.http.post<StorefrontSection[]>(
      `${this.apiBase}/api/v1/marketplace/storefront/sections/reorder?storeId=${storeId}`,
      payload);
  }

  listStorefrontSectionItems(sectionId: string): Observable<StorefrontSectionItem[]> {
    return this.http.get<StorefrontSectionItem[]>(`${this.apiBase}/api/v1/marketplace/storefront/sections/${sectionId}/items`);
  }

  upsertStorefrontSectionItem(sectionId: string, payload: UpsertStorefrontSectionItemPayload): Observable<StorefrontSectionItem> {
    return this.http.post<StorefrontSectionItem>(`${this.apiBase}/api/v1/marketplace/storefront/sections/${sectionId}/items`, payload);
  }

  removeStorefrontSectionItem(sectionId: string, itemId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiBase}/api/v1/marketplace/storefront/sections/${sectionId}/items/${itemId}`);
  }

  reorderStorefrontSectionItems(sectionId: string, payload: ReorderSectionItemPayload[]): Observable<StorefrontSectionItem[]> {
    return this.http.post<StorefrontSectionItem[]>(
      `${this.apiBase}/api/v1/marketplace/storefront/sections/${sectionId}/items/reorder`,
      payload);
  }

  listStorefrontNavigationItems(storeId: string, locationKey?: string): Observable<StorefrontNavigationItem[]> {
    const params = new URLSearchParams();
    params.set('storeId', storeId);
    if (locationKey) params.set('locationKey', locationKey);
    return this.http.get<StorefrontNavigationItem[]>(
      `${this.apiBase}/api/v1/marketplace/storefront/navigation-items?${params.toString()}`);
  }

  upsertStorefrontNavigationItem(payload: UpsertStorefrontNavigationItemPayload): Observable<StorefrontNavigationItem> {
    return this.http.post<StorefrontNavigationItem>(`${this.apiBase}/api/v1/marketplace/storefront/navigation-items`, payload);
  }

  reorderStorefrontNavigationItems(storeId: string, payload: ReorderNavigationItemPayload[]): Observable<StorefrontNavigationItem[]> {
    return this.http.post<StorefrontNavigationItem[]>(
      `${this.apiBase}/api/v1/marketplace/storefront/navigation-items/reorder?storeId=${storeId}`,
      payload);
  }

  listStorefrontContentBlocks(storeId: string, placementKey?: string): Observable<StorefrontContentBlock[]> {
    const params = new URLSearchParams();
    params.set('storeId', storeId);
    if (placementKey) params.set('placementKey', placementKey);
    return this.http.get<StorefrontContentBlock[]>(
      `${this.apiBase}/api/v1/marketplace/storefront/content-blocks?${params.toString()}`);
  }

  upsertStorefrontContentBlock(payload: UpsertStorefrontContentBlockPayload): Observable<StorefrontContentBlock> {
    return this.http.post<StorefrontContentBlock>(`${this.apiBase}/api/v1/marketplace/storefront/content-blocks`, payload);
  }

  publishStorefrontContentBlock(id: string): Observable<StorefrontContentBlock> {
    return this.http.post<StorefrontContentBlock>(`${this.apiBase}/api/v1/marketplace/storefront/content-blocks/${id}/publish`, {});
  }

  archiveStorefrontContentBlock(id: string): Observable<StorefrontContentBlock> {
    return this.http.post<StorefrontContentBlock>(`${this.apiBase}/api/v1/marketplace/storefront/content-blocks/${id}/archive`, {});
  }

  listFinancialPostingRecords(): Observable<CatalogFinancialPostingRecord[]> {
    return this.http.get<CatalogFinancialPostingRecord[]>(`${this.apiBase}/api/v1/catalog/operations/financial-postings`);
  }

  listAssets(status?: string): Observable<AssetMaster[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<AssetMaster[]>(`${this.apiBase}/api/v1/assets${query ? `?${query}` : ''}`);
  }

  getAsset(id: string): Observable<AssetMaster> {
    return this.http.get<AssetMaster>(`${this.apiBase}/api/v1/assets/${id}`);
  }

  registerAsset(payload: RegisterAssetPayload): Observable<AssetMaster> {
    return this.http.post<AssetMaster>(`${this.apiBase}/api/v1/assets/register`, payload);
  }

  listAssetCategories(): Observable<AssetCategory[]> {
    return this.http.get<AssetCategory[]>(`${this.apiBase}/api/v1/assets/categories`);
  }

  createAssetCategory(payload: CreateAssetCategoryPayload): Observable<AssetCategory> {
    return this.http.post<AssetCategory>(`${this.apiBase}/api/v1/assets/categories`, payload);
  }

  listAssetBooks(): Observable<AssetBook[]> {
    return this.http.get<AssetBook[]>(`${this.apiBase}/api/v1/assets/books`);
  }

  createAssetBook(payload: CreateAssetBookPayload): Observable<AssetBook> {
    return this.http.post<AssetBook>(`${this.apiBase}/api/v1/assets/books`, payload);
  }

  runDepreciation(payload: RunDepreciationPayload): Observable<AssetDepreciationRun> {
    return this.http.post<AssetDepreciationRun>(`${this.apiBase}/api/v1/assets/depreciation/runs`, payload);
  }

  listRentalAgreements(status?: string): Observable<RentalAgreement[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<RentalAgreement[]>(`${this.apiBase}/api/v1/rental/agreements${query ? `?${query}` : ''}`);
  }

  getRentalAgreement(id: string): Observable<RentalAgreement> {
    return this.http.get<RentalAgreement>(`${this.apiBase}/api/v1/rental/agreements/${id}`);
  }

  createRentalAgreement(payload: CreateRentalAgreementPayload): Observable<RentalAgreement> {
    return this.http.post<RentalAgreement>(`${this.apiBase}/api/v1/rental/agreements`, payload);
  }

  handoverRentalAgreement(id: string, payload: RentalHandoverPayload): Observable<RentalAgreement> {
    return this.http.post<RentalAgreement>(`${this.apiBase}/api/v1/rental/agreements/${id}/handover`, payload);
  }

  returnRentalAgreement(id: string, payload: RentalReturnPayload): Observable<RentalAgreement> {
    return this.http.post<RentalAgreement>(`${this.apiBase}/api/v1/rental/agreements/${id}/return`, payload);
  }

  assessRentalDamage(id: string, payload: RentalDamageAssessmentPayload): Observable<RentalDamageAssessment> {
    return this.http.post<RentalDamageAssessment>(`${this.apiBase}/api/v1/rental/agreements/${id}/damage-assessment`, payload);
  }

  approveRentalDamage(id: string): Observable<RentalDamageAssessment> {
    return this.http.post<RentalDamageAssessment>(`${this.apiBase}/api/v1/rental/agreements/${id}/damage-assessment/approve`, {});
  }

  settleRentalAgreement(id: string, payload: RentalSettlePayload): Observable<RentalSettlement> {
    return this.http.post<RentalSettlement>(`${this.apiBase}/api/v1/rental/agreements/${id}/settle`, payload);
  }

  listRentalUnits(): Observable<RentalAssetUnit[]> {
    return this.http.get<RentalAssetUnit[]>(`${this.apiBase}/api/v1/rental/units`);
  }

  getRentalBookingBoard(fromUtc?: string, toUtc?: string): Observable<RentalBookingBoardEntry[]> {
    const params = new URLSearchParams();
    if (fromUtc) params.set('fromUtc', fromUtc);
    if (toUtc) params.set('toUtc', toUtc);
    const query = params.toString();
    return this.http.get<RentalBookingBoardEntry[]>(`${this.apiBase}/api/v1/rental/booking-board${query ? `?${query}` : ''}`);
  }

  listRentalBookings(status?: string): Observable<RentalBooking[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<RentalBooking[]>(`${this.apiBase}/api/v1/rental/bookings${query ? `?${query}` : ''}`);
  }

  createRentalBooking(payload: CreateRentalBookingPayload): Observable<RentalBooking> {
    return this.http.post<RentalBooking>(`${this.apiBase}/api/v1/rental/bookings`, payload);
  }

  listHospitalityProperties(): Observable<HospitalityProperty[]> {
    return this.http.get<HospitalityProperty[]>(`${this.apiBase}/api/v1/hospitality/properties`);
  }

  getHospitalityProperty(id: string): Observable<HospitalityProperty> {
    return this.http.get<HospitalityProperty>(`${this.apiBase}/api/v1/hospitality/properties/${id}`);
  }

  createHospitalityProperty(payload: CreateHospitalityPropertyPayload): Observable<HospitalityProperty> {
    return this.http.post<HospitalityProperty>(`${this.apiBase}/api/v1/hospitality/properties`, payload);
  }

  updateHospitalityProperty(id: string, payload: UpdateHospitalityPropertyPayload): Observable<HospitalityProperty> {
    return this.http.put<HospitalityProperty>(`${this.apiBase}/api/v1/hospitality/properties/${id}`, payload);
  }

  listHospitalityRoomTypes(propertyId?: string): Observable<HospitalityRoomType[]> {
    const params = new URLSearchParams();
    if (propertyId) params.set('propertyId', propertyId);
    const query = params.toString();
    return this.http.get<HospitalityRoomType[]>(`${this.apiBase}/api/v1/hospitality/room-types${query ? `?${query}` : ''}`);
  }

  createHospitalityRoomType(payload: CreateHospitalityRoomTypePayload): Observable<HospitalityRoomType> {
    return this.http.post<HospitalityRoomType>(`${this.apiBase}/api/v1/hospitality/room-types`, payload);
  }

  listHospitalityRooms(propertyId?: string, roomTypeId?: string, status?: string): Observable<HospitalityRoomUnit[]> {
    const params = new URLSearchParams();
    if (propertyId) params.set('propertyId', propertyId);
    if (roomTypeId) params.set('roomTypeId', roomTypeId);
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<HospitalityRoomUnit[]>(`${this.apiBase}/api/v1/hospitality/rooms${query ? `?${query}` : ''}`);
  }

  createHospitalityRoom(payload: CreateHospitalityRoomPayload): Observable<HospitalityRoomUnit> {
    return this.http.post<HospitalityRoomUnit>(`${this.apiBase}/api/v1/hospitality/rooms`, payload);
  }

  updateHospitalityRoomStatus(id: string, status: string): Observable<HospitalityRoomUnit> {
    return this.http.post<HospitalityRoomUnit>(`${this.apiBase}/api/v1/hospitality/rooms/${id}/status`, { status });
  }

  getHospitalityAvailability(payload: HospitalityAvailabilityQuery): Observable<HospitalityAvailability> {
    const params = new URLSearchParams();
    params.set('propertyId', payload.propertyId);
    params.set('roomTypeId', payload.roomTypeId);
    params.set('checkInDateUtc', payload.checkInDateUtc);
    params.set('checkOutDateUtc', payload.checkOutDateUtc);
    if (payload.ratePlanId) params.set('ratePlanId', payload.ratePlanId);
    return this.http.get<HospitalityAvailability>(`${this.apiBase}/api/v1/hospitality/availability?${params.toString()}`);
  }

  listHospitalityRatePlans(propertyId?: string): Observable<HospitalityRatePlan[]> {
    const params = new URLSearchParams();
    if (propertyId) params.set('propertyId', propertyId);
    const query = params.toString();
    return this.http.get<HospitalityRatePlan[]>(`${this.apiBase}/api/v1/hospitality/rate-plans${query ? `?${query}` : ''}`);
  }

  createHospitalityRatePlan(payload: CreateHospitalityRatePlanPayload): Observable<HospitalityRatePlan> {
    return this.http.post<HospitalityRatePlan>(`${this.apiBase}/api/v1/hospitality/rate-plans`, payload);
  }

  listHospitalityRateCalendar(query: HospitalityRateCalendarQuery): Observable<HospitalityRateCalendarEntry[]> {
    const params = new URLSearchParams();
    params.set('propertyId', query.propertyId);
    if (query.ratePlanId) params.set('ratePlanId', query.ratePlanId);
    if (query.roomTypeId) params.set('roomTypeId', query.roomTypeId);
    if (query.fromUtc) params.set('fromUtc', query.fromUtc);
    if (query.toUtc) params.set('toUtc', query.toUtc);
    return this.http.get<HospitalityRateCalendarEntry[]>(`${this.apiBase}/api/v1/hospitality/rate-calendar?${params.toString()}`);
  }

  createHospitalityRateCalendarEntry(payload: CreateHospitalityRateCalendarEntryPayload): Observable<HospitalityRateCalendarEntry> {
    return this.http.post<HospitalityRateCalendarEntry>(`${this.apiBase}/api/v1/hospitality/rate-calendar`, payload);
  }

  deleteHospitalityRateCalendarEntry(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiBase}/api/v1/hospitality/rate-calendar/${id}`);
  }

  getHospitalityFrontDeskBoard(propertyId: string): Observable<HospitalityFrontDeskBoard> {
    return this.http.get<HospitalityFrontDeskBoard>(
      `${this.apiBase}/api/v1/hospitality/front-desk/board?propertyId=${propertyId}`
    );
  }

  getHospitalityBookingBoard(propertyId: string, fromUtc?: string, toUtc?: string): Observable<HospitalityBookingBoardEntry[]> {
    const params = new URLSearchParams({ propertyId });
    if (fromUtc) params.set('fromUtc', fromUtc);
    if (toUtc) params.set('toUtc', toUtc);
    return this.http.get<HospitalityBookingBoardEntry[]>(`${this.apiBase}/api/v1/hospitality/booking-board?${params.toString()}`);
  }

  listHospitalityReservations(propertyId?: string, status?: string, search?: string): Observable<HospitalityReservation[]> {
    const params = new URLSearchParams();
    if (propertyId) params.set('propertyId', propertyId);
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    const query = params.toString();
    return this.http.get<HospitalityReservation[]>(`${this.apiBase}/api/v1/hospitality/reservations${query ? `?${query}` : ''}`);
  }

  getHospitalityReservation(id: string): Observable<HospitalityReservation> {
    return this.http.get<HospitalityReservation>(`${this.apiBase}/api/v1/hospitality/reservations/${id}`);
  }

  getHospitalityStayByReservation(reservationId: string): Observable<HospitalityStay> {
    return this.http.get<HospitalityStay>(`${this.apiBase}/api/v1/hospitality/reservations/${reservationId}/stay`);
  }

  createHospitalityReservation(payload: CreateHospitalityReservationPayload): Observable<HospitalityReservation> {
    return this.http.post<HospitalityReservation>(`${this.apiBase}/api/v1/hospitality/reservations`, payload);
  }

  checkInHospitalityReservation(id: string, payload?: HospitalityCheckInPayload): Observable<HospitalityStay> {
    return this.http.post<HospitalityStay>(`${this.apiBase}/api/v1/hospitality/reservations/${id}/check-in`, payload ?? {});
  }

  getHospitalityStay(id: string): Observable<HospitalityStay> {
    return this.http.get<HospitalityStay>(`${this.apiBase}/api/v1/hospitality/stays/${id}`);
  }

  postHospitalityFolioCharge(stayId: string, payload: PostHospitalityFolioChargePayload): Observable<HospitalityStay> {
    return this.http.post<HospitalityStay>(`${this.apiBase}/api/v1/hospitality/stays/${stayId}/post-charge`, payload);
  }

  checkOutHospitalityStay(stayId: string): Observable<HospitalityCheckOutResult> {
    return this.http.post<HospitalityCheckOutResult>(`${this.apiBase}/api/v1/hospitality/stays/${stayId}/check-out`, {});
  }

  receiveHospitalityPrepayment(reservationId: string, payload: ReceiveHospitalityPrepaymentPayload): Observable<HospitalityPrepayment> {
    return this.http.post<HospitalityPrepayment>(`${this.apiBase}/api/v1/hospitality/reservations/${reservationId}/prepayment`, payload);
  }

  postHospitalityRefund(reservationId: string, payload: PostHospitalityRefundPayload): Observable<HospitalityRefund> {
    return this.http.post<HospitalityRefund>(`${this.apiBase}/api/v1/hospitality/reservations/${reservationId}/refund`, payload);
  }

  postHospitalityChannelCommission(reservationId: string, payload: PostHospitalityChannelCommissionPayload): Observable<HospitalityChannelCommission> {
    return this.http.post<HospitalityChannelCommission>(`${this.apiBase}/api/v1/hospitality/reservations/${reservationId}/channel-commission`, payload);
  }

  getHospitalityCancellationPolicy(propertyId: string): Observable<HospitalityCancellationPolicy> {
    return this.http.get<HospitalityCancellationPolicy>(`${this.apiBase}/api/v1/hospitality/properties/${propertyId}/cancellation-policy`);
  }

  updateHospitalityCancellationPolicy(propertyId: string, payload: UpdateHospitalityCancellationPolicyPayload): Observable<HospitalityCancellationPolicy> {
    return this.http.put<HospitalityCancellationPolicy>(`${this.apiBase}/api/v1/hospitality/properties/${propertyId}/cancellation-policy`, payload);
  }

  getHospitalityOverbookingPolicy(propertyId: string): Observable<HospitalityOverbookingPolicy> {
    return this.http.get<HospitalityOverbookingPolicy>(`${this.apiBase}/api/v1/hospitality/properties/${propertyId}/overbooking-policy`);
  }

  updateHospitalityOverbookingPolicy(propertyId: string, payload: UpdateHospitalityOverbookingPolicyPayload): Observable<HospitalityOverbookingPolicy> {
    return this.http.put<HospitalityOverbookingPolicy>(`${this.apiBase}/api/v1/hospitality/properties/${propertyId}/overbooking-policy`, payload);
  }

  cancelHospitalityReservation(id: string, payload: CancelHospitalityReservationPayload): Observable<{ id: string; status: string }> {
    return this.http.post<{ id: string; status: string }>(`${this.apiBase}/api/v1/hospitality/reservations/${id}/cancel`, payload);
  }

  markHospitalityNoShow(id: string, payload: MarkHospitalityNoShowPayload): Observable<{ id: string; status: string }> {
    return this.http.post<{ id: string; status: string }>(`${this.apiBase}/api/v1/hospitality/reservations/${id}/no-show`, payload);
  }

  listAssignableRooms(propertyId: string, roomTypeId?: string): Observable<HospitalityAssignableRoom[]> {
    const params = new URLSearchParams();
    if (roomTypeId) params.set('roomTypeId', roomTypeId);
    const query = params.toString();
    return this.http.get<HospitalityAssignableRoom[]>(
      `${this.apiBase}/api/v1/hospitality/properties/${propertyId}/assignable-rooms${query ? `?${query}` : ''}`);
  }

  listChannelRateRules(propertyId: string): Observable<HospitalityChannelRateRule[]> {
    return this.http.get<HospitalityChannelRateRule[]>(`${this.apiBase}/api/v1/hospitality/channel-rate-rules?propertyId=${propertyId}`);
  }

  createChannelRateRule(payload: CreateHospitalityChannelRateRulePayload): Observable<HospitalityChannelRateRule> {
    return this.http.post<HospitalityChannelRateRule>(`${this.apiBase}/api/v1/hospitality/channel-rate-rules`, payload);
  }

  updateChannelRateRule(id: string, payload: UpdateHospitalityChannelRateRulePayload): Observable<HospitalityChannelRateRule> {
    return this.http.put<HospitalityChannelRateRule>(`${this.apiBase}/api/v1/hospitality/channel-rate-rules/${id}`, payload);
  }

  postMiniBarConsumption(stayId: string, payload: PostMiniBarConsumptionPayload): Observable<MiniBarConsumption> {
    return this.http.post<MiniBarConsumption>(`${this.apiBase}/api/v1/hospitality/stays/${stayId}/minibar-consumption`, payload);
  }

  createRoomMaintenanceRequest(payload: CreateRoomMaintenanceRequestPayload): Observable<RoomMaintenanceRequest> {
    return this.http.post<RoomMaintenanceRequest>(`${this.apiBase}/api/v1/hospitality/room-maintenance/requests`, payload);
  }

  listRoomMaintenanceRequests(status?: string): Observable<RoomMaintenanceRequest[]> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.http.get<RoomMaintenanceRequest[]>(`${this.apiBase}/api/v1/hospitality/room-maintenance/requests${query}`);
  }

  listPolicyExceptions(propertyId?: string, status?: string): Observable<HospitalityPolicyException[]> {
    const params = new URLSearchParams();
    if (propertyId) params.set('propertyId', propertyId);
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<HospitalityPolicyException[]>(`${this.apiBase}/api/v1/hospitality/policy-exceptions${query ? `?${query}` : ''}`);
  }

  createPolicyException(payload: CreateHospitalityPolicyExceptionPayload): Observable<HospitalityPolicyException> {
    return this.http.post<HospitalityPolicyException>(`${this.apiBase}/api/v1/hospitality/policy-exceptions`, payload);
  }

  resolvePolicyException(id: string, payload: ResolveHospitalityPolicyExceptionPayload): Observable<HospitalityPolicyException> {
    return this.http.post<HospitalityPolicyException>(`${this.apiBase}/api/v1/hospitality/policy-exceptions/${id}/resolve`, payload);
  }

  assignHousekeepingTask(id: string, assignedToReference: string): Observable<HousekeepingTaskDetail> {
    return this.http.post<HousekeepingTaskDetail>(`${this.apiBase}/api/v1/hospitality/housekeeping/tasks/${id}/assign`, { assignedToReference });
  }

  getHospitalityKpi(propertyId: string, fromDate?: string, toDate?: string): Observable<HospitalityKpi> {
    const params = new URLSearchParams({ propertyId });
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    return this.http.get<HospitalityKpi>(`${this.apiBase}/api/v1/hospitality/kpi?${params.toString()}`);
  }

  runHospitalityReconciliation(payload: RunHospitalityReconciliationPayload): Observable<HospitalityReconciliation> {
    return this.http.post<HospitalityReconciliation>(`${this.apiBase}/api/v1/hospitality/reconciliation/run`, payload);
  }

  listHospitalityReconciliations(propertyId: string): Observable<HospitalityReconciliation[]> {
    return this.http.get<HospitalityReconciliation[]>(`${this.apiBase}/api/v1/hospitality/reconciliation?propertyId=${propertyId}`);
  }

  seedHospitalityDemo(): Observable<HospitalityDemoSeedResult> {
    return this.http.post<HospitalityDemoSeedResult>(`${this.apiBase}/api/v1/hospitality/demo-seed`, {});
  }

  listHousekeepingTasks(status?: string): Observable<HousekeepingTaskDetail[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<HousekeepingTaskDetail[]>(`${this.apiBase}/api/v1/hospitality/housekeeping/tasks${query ? `?${query}` : ''}`);
  }

  completeHousekeepingTask(id: string, roomStatusAfterClean?: string): Observable<HousekeepingTaskDetail> {
    return this.http.post<HousekeepingTaskDetail>(`${this.apiBase}/api/v1/hospitality/housekeeping/tasks/${id}/complete`, { roomStatusAfterClean: roomStatusAfterClean ?? 'Inspected' });
  }

  listMaintenancePlans(assetId?: string): Observable<MaintenancePlan[]> {
    const params = new URLSearchParams();
    if (assetId) params.set('assetId', assetId);
    const query = params.toString();
    return this.http.get<MaintenancePlan[]>(`${this.apiBase}/api/v1/cmms/plans${query ? `?${query}` : ''}`);
  }

  createMaintenancePlan(payload: CreateMaintenancePlanPayload): Observable<MaintenancePlan> {
    return this.http.post<MaintenancePlan>(`${this.apiBase}/api/v1/cmms/plans`, payload);
  }

  addAssetInsurance(assetId: string, payload: AddAssetInsurancePayload): Observable<AssetInsurancePolicy> {
    return this.http.post<AssetInsurancePolicy>(`${this.apiBase}/api/v1/assets/${assetId}/insurance`, payload);
  }

  listWorkOrders(status?: string): Observable<WorkOrder[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.http.get<WorkOrder[]>(`${this.apiBase}/api/v1/cmms/work-orders${query ? `?${query}` : ''}`);
  }

  getWorkOrder(id: string): Observable<WorkOrder> {
    return this.http.get<WorkOrder>(`${this.apiBase}/api/v1/cmms/work-orders/${id}`);
  }

  createWorkOrder(payload: CreateWorkOrderPayload): Observable<WorkOrder> {
    return this.http.post<WorkOrder>(`${this.apiBase}/api/v1/cmms/work-orders`, payload);
  }

  assignWorkOrder(id: string, resourceId: string): Observable<WorkOrder> {
    return this.http.post<WorkOrder>(`${this.apiBase}/api/v1/cmms/work-orders/${id}/assign`, { resourceId });
  }

  startWorkOrder(id: string): Observable<WorkOrder> {
    return this.http.post<WorkOrder>(`${this.apiBase}/api/v1/cmms/work-orders/${id}/start`, {});
  }

  completeWorkOrder(id: string, payload: CompleteWorkOrderPayload): Observable<WorkOrder> {
    return this.http.post<WorkOrder>(`${this.apiBase}/api/v1/cmms/work-orders/${id}/complete`, payload);
  }

  consumeWorkOrderParts(id: string, payload: ConsumeWorkOrderPartsPayload): Observable<WorkOrderConsumption> {
    return this.http.post<WorkOrderConsumption>(`${this.apiBase}/api/v1/cmms/work-orders/${id}/consume-parts`, payload);
  }

  getDowntimeDashboard(): Observable<DowntimeDashboard> {
    return this.http.get<DowntimeDashboard>(`${this.apiBase}/api/v1/cmms/downtime/dashboard`);
  }

  importLegacyAssets(payload: LegacyAssetImportPayload): Observable<LegacyAssetImportResult> {
    return this.http.post<LegacyAssetImportResult>(`${this.apiBase}/api/v1/assets/import/legacy`, payload);
  }

  previewNextRunningNumber(entityType: string, parentEntityType?: string, parentEntityId?: string): Observable<string> {
    const params: Record<string, string> = {
      entityType,
      silent: 'true'
    };
    if (parentEntityType?.trim()) {
      params['parentEntityType'] = parentEntityType.trim();
    }
    if (parentEntityId?.trim()) {
      params['parentEntityId'] = parentEntityId.trim();
    }
    return this.http
      .get<{ preview?: string; Preview?: string }>(`${this.apiBase}/api/v1/platform/running-numbers/preview-next`, { params })
      .pipe(map(response => (response.preview ?? response.Preview ?? '').trim()));
  }
}

export interface ServiceCompletionEvidence {
  id: string;
  serviceOrderId: string;
  evidenceType: string;
  contentReference: string;
  caption: string;
  createdAtUtc: string;
}

export interface AddServiceCompletionEvidencePayload {
  evidenceType: string;
  contentReference: string;
  caption: string;
}

export interface ServiceSlaEscalation {
  id: string;
  serviceOrderId: string;
  escalatedAtUtc: string;
  reason: string;
  status: string;
}

export interface BootstrapPhase3PostingResult {
  financialBookId: string;
  financialBookCode: string;
  postingEnabled: boolean;
  currencyCode: string;
  assetBooksUpdated: number;
}

export interface BootstrapPhase4PostingResult {
  financialBookId: string;
  financialBookCode: string;
  postingEnabled: boolean;
  currencyCode: string;
  hospitalityPostingAccountsConfigured: boolean;
}

export interface BootstrapPhase5PostingResult {
  financialBookId: string;
  financialBookCode: string;
  postingEnabled: boolean;
  currencyCode: string;
  phase5PostingAccountsConfigured: boolean;
}

export interface DigitalEntitlement {
  id: string;
  offeringId: string;
  customerReference: string;
  status: string;
  saleAmount: number;
  currencyCode: string;
  usageCreditsGranted: number;
  usageCreditsConsumed: number;
  remainingUsageCredits: number;
  accessStartsAtUtc?: string | null;
  accessEndsAtUtc?: string | null;
  revokedAtUtc?: string | null;
}

export interface DigitalLicenseSummary {
  id: string;
  keyPrefix: string;
  status: string;
  createdAtUtc: string;
}

export interface DigitalEntitlementDetail extends DigitalEntitlement {
  customerId?: string | null;
  licenses: DigitalLicenseSummary[];
}

export interface DigitalLicense {
  id: string;
  entitlementId: string;
  keyPrefix: string;
  licenseKey: string;
  status: string;
}

export interface RunProductSubscriptionBillingResult {
  dueCount: number;
  issuedCount: number;
}

export interface ProcessProductSubscriptionLifecycleResult {
  enteredGraceCount: number;
  expiredCount: number;
  dueCount: number;
  issuedCount: number;
}

export interface RegulatedFinanceDashboard {
  preciousMetalOrderCount: number;
  brokerageOrderCount: number;
  insurancePolicyCount: number;
  openInsuranceClaimCount: number;
  fxTradeCount: number;
  complianceScreeningCount: number;
  hardStopCount: number;
  recentPreciousMetalOrders: PreciousMetalOrder[];
  recentBrokerageOrders: BrokerageOrder[];
  recentInsurancePolicies: InsurancePolicy[];
  recentInsuranceClaims: InsuranceClaim[];
  recentFxTrades: FxTrade[];
  recentComplianceScreens: ComplianceScreenResult[];
}

export interface BrokerageAccount {
  id: string;
  accountNumber: string;
  suitabilityApproved: boolean;
}

export interface PreciousMetalOrder {
  id: string;
  side: string;
  status: string;
  quantityGrams: number;
}

export interface BrokerageOrder {
  id: string;
  accountId: string;
  symbol: string;
  status: string;
}

export interface InsurancePolicy {
  id: string;
  policyNumber: string;
  premiumAmount: number;
  currencyCode: string;
}

export interface InsuranceClaim {
  id: string;
  policyId: string;
  claimNumber: string;
  reserveAmount: number;
  status: string;
}

export interface FxTrade {
  id: string;
  status: string;
  baseAmount: number;
  rate: number;
}

export interface ComplianceScreenResult {
  id: string;
  result: string;
  isHardStop: boolean;
  decisionNotes: string;
}

export interface UnifiedCommerceOrder {
  id: string;
  offeringId: string;
  customerId?: string | null;
  customerReference: string;
  grossAmount: number;
  currencyCode: string;
  status: string;
  fulfillmentChannel: string;
  fulfillmentReferenceId?: string | null;
  paymentCapturedAtUtc?: string | null;
  fulfilledAtUtc?: string | null;
  completedAtUtc?: string | null;
  errorCode?: string;
}

export interface ProductSubscriptionPlan {
  id: string;
  offeringId: string;
  planCode: string;
  name: string;
  billingCycle: string;
  recurringAmount: number;
  currencyCode: string;
  gracePeriodDays: number;
  isActive: boolean;
}

export interface ProductSubscriptionContract {
  id: string;
  planId: string;
  offeringId: string;
  customerReference: string;
  status: string;
  startedAtUtc: string;
  currentPeriodEndUtc: string;
  graceEndsAtUtc?: string | null;
  cancelledAtUtc?: string | null;
}

export interface ProductSubscriptionInvoice {
  id: string;
  contractId: string;
  invoiceNumber: string;
  status: string;
  dueAtUtc: string;
  amount: number;
  currencyCode: string;
  recognizedAmount: number;
  isRecognized: boolean;
  issuedAtUtc?: string | null;
  recognizedAtUtc?: string | null;
}

export interface ProductSubscriptionContractDetail {
  contract: ProductSubscriptionContract;
  invoices: ProductSubscriptionInvoice[];
  lifecycleEvents: { id: string; eventType: string; notes: string; createdAtUtc: string }[];
}

export interface MarketplaceOfferingProposal {
  id: string;
  sellerId: string;
  sellerCode?: string;
  sellerDisplayName?: string;
  kind: string;
  status: string;
  targetOfferingId?: string | null;
  proposedCode: string;
  proposedTitle: string;
  proposedSlug: string;
  proposedShortDescription: string;
  proposedFullDescription: string;
  proposedBasePrice: number;
  proposedCurrency: string;
  proposedOfferingType: string;
  offerPrice: number;
  offerCurrencyCode: string;
  fulfillmentSlaHours: number;
  storeId?: string | null;
  reviewNotes: string;
  reviewActorReference: string;
  reviewedAtUtc?: string | null;
  resultOfferingId?: string | null;
  resultOfferId?: string | null;
  createdAtUtc: string;
}

export interface MarketplaceCommissionRuleRecord {
  id: string;
  ruleCode: string;
  name: string;
  scopeKind: string;
  ruleType: string;
  ratePercent: number;
  fixedAmount: number;
  minLineGross: number;
  maxCommissionAmount: number | null;
  priority: number;
  effectiveFromUtc: string;
  effectiveUntilUtc: string | null;
  isActive: boolean;
  storeId: string | null;
  sellerId: string | null;
  categoryId: string | null;
  offeringId: string | null;
  migratedFromCommissionPolicyId: string | null;
}

export interface CreateMarketplaceCommissionRulePayload {
  ruleCode: string;
  name: string;
  scopeKind: string;
  ruleType: string;
  ratePercent: number;
  fixedAmount: number;
  minLineGross: number;
  maxCommissionAmount: number | null;
  priority: number;
  effectiveFromUtc: string;
  effectiveUntilUtc: string | null;
  storeId: string | null;
  sellerId: string | null;
  categoryId: string | null;
  offeringId: string | null;
}

export interface MarketplaceSellerTierBandRecord {
  id: string;
  tierCode: string;
  displayName: string;
  minScore: number;
  commissionDiscountPercent: number;
  buyBoxScoreAdjustment: number;
  sortOrder: number;
  isActive: boolean;
}

export interface MarketplaceSellerTierProfileRecord {
  sellerId: string;
  computedScore: number;
  computedTierCode: string;
  effectiveTierCode: string;
  overrideTierCode: string | null;
  overrideReason: string | null;
  overrideUntilUtc: string | null;
  lastScoredAtUtc: string | null;
  scoringEvidenceJson: string;
}

export interface MarketplaceSellerTierOverridePayload {
  tierCode: string | null;
  reason: string | null;
  actorReference: string | null;
  untilUtc: string | null;
}

export interface RetailMediaCampaignRecord {
  id: string;
  sellerId: string;
  campaignCode: string;
  name: string;
  status: string;
  goal: string;
  currencyCode: string;
  dailyBudget: number | null;
  lifetimeBudget: number | null;
  spentAmount: number;
  startsAtUtc: string | null;
  endsAtUtc: string | null;
}

export interface SimulateMarketplaceCommissionPayload {
  asOfUtc: string | null;
  lines: Array<{
    lineNumber: number;
    lineGrossAmount: number;
    sellerId: string | null;
    offeringId: string;
    categoryId: string | null;
    storeId: string | null;
  }>;
}

export interface MarketplaceCommissionSimulationResult {
  totalCommissionAmount: number;
  lines: Array<{
    lineNumber: number;
    commissionAmount: number;
    snapshot: {
      ruleId: string;
      ruleCode: string;
      scopeKind: string;
      ruleType: string;
      ratePercent: number;
      fixedAmount: number;
      basisAmount: number;
      commissionAmount: number;
      resolvedAtUtc: string;
    };
  }>;
}

export interface MarketplaceSeller {
  id: string;
  sellerCode: string;
  displayName: string;
  status: string;
  kycReference: string;
  partyId?: string | null;
  customerAccountId?: string | null;
  publicProfileSummary?: string;
  returnPolicySummary?: string;
  slaPolicySummary?: string;
  commissionPolicyId?: string | null;
  payoutProfileReference?: string;
  complianceEvidenceReference?: string;
  lastStatusReason?: string;
  lastStatusActorReference?: string;
  lastStatusChangedAtUtc?: string | null;
  approvedAtUtc?: string | null;
}

export interface MarketplaceSettlementSummary {
  id: string;
  sellerId: string;
  orderReferenceId: string;
  netPayoutAmount: number;
  currencyCode: string;
  createdAtUtc: string;
}

export interface MarketplaceDispute {
  id: string;
  orderReferenceId: string;
  sellerId: string;
  status: string;
  reason: string;
  disputedAmount: number;
  dualApprovalRequired: boolean;
  firstApproverReference: string;
  secondApproverReference: string | null;
  resolutionNotes: string;
  resolvedAtUtc: string | null;
}

export interface MarketplaceDashboard {
  approvedSellerCount: number;
  pendingSellerCount: number;
  openDisputeCount: number;
  heldEscrowCount: number;
  totalSettledNetPayout: number;
  recentSettlements: MarketplaceSettlementSummary[];
  openDisputes: MarketplaceDispute[];
}

export interface LaunchReadinessCriterion {
  key: string;
  domain: string;
  owner: string;
  evidenceSource: string;
  threshold: string;
  status: string;
  isBlocker: boolean;
  notes: string | null;
}

export interface LaunchReadinessConfigCheck {
  key: string;
  label: string;
  currentValue: string;
  expectedProductionValue: string;
  status: string;
  isBlocker: boolean;
  notes: string | null;
}

export interface LaunchReadinessKpiBaseline {
  key: string;
  label: string;
  owner: string;
  threshold: string;
  measurementSource: string;
  alertContractStatus: string;
  runbookKey: string | null;
}

export interface LaunchReadinessPerformanceBudget {
  key: string;
  label: string;
  p95TargetMs: number;
  measurementSource: string;
  notes: string | null;
}

export interface LaunchReadinessCacheBudget {
  key: string;
  label: string;
  maxStalenessSeconds: number;
  notes: string;
}

export interface LaunchReadinessSyntheticCheckoutDrill {
  key: string;
  cadence: string;
  contractVersion: string;
  lastRecordedAtUtc: string;
  evidenceRule: string;
  requiredChecks: string[];
}

export interface LaunchReadinessEvidenceItem {
  key: string;
  label: string;
  status: string;
  lastRecordedAtUtc: string | null;
  evidenceReference: string | null;
  isBlocker: boolean;
  notes: string | null;
}

export interface LaunchReadinessObservabilityCheck {
  key: string;
  label: string;
  endpointOrSource: string;
  status: string;
  notes: string | null;
}

export interface LaunchReadinessSeededConfigItem {
  key: string;
  label: string;
  currentValue: string;
  status: string;
  notes: string | null;
}

export interface LaunchReadinessProviderMode {
  domain: string;
  mode: string;
  status: string;
  notes: string | null;
}

export interface LaunchReadinessHypercareSchedule {
  windowDays: number;
  cadence: string;
  escalationPath: string;
  notes: string;
}

export interface MarketplaceLaunchReadinessSummary {
  launchDecision: string;
  readyCount: number;
  conditionalCount: number;
  blockerCount: number;
  deferralCount: number;
  rolloutStage: string;
  generatedAtUtc: string;
  goNoGoCriteria: LaunchReadinessCriterion[];
  configChecks: LaunchReadinessConfigCheck[];
  kpiBaselines: LaunchReadinessKpiBaseline[];
  performanceBudgets: LaunchReadinessPerformanceBudget[];
  cacheBudgets: LaunchReadinessCacheBudget[];
  sloBreachReasonCodes: string[];
  syntheticCheckoutDrill: LaunchReadinessSyntheticCheckoutDrill;
  operationalEvidence: LaunchReadinessEvidenceItem[];
  observabilityChecks: LaunchReadinessObservabilityCheck[];
  seededConfigStatus: LaunchReadinessSeededConfigItem[];
  providerModes: LaunchReadinessProviderMode[];
  hypercareSchedule: LaunchReadinessHypercareSchedule;
  runbookKeys: string[];
  acceptedDeferrals: string[];
  launchBlockers: string[];
}

export interface MarketplaceStore {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  storeKey: string;
  name: string;
  status: string;
  defaultCulture: string;
  defaultCurrency: string;
  featureFlagsJson: string;
}

export interface UpsertMarketplaceStorePayload {
  storeKey: string;
  name: string;
  defaultCulture: string;
  defaultCurrency: string;
  status?: string | null;
  featureFlagsJson?: string | null;
}

export interface StoreBranding {
  storeId: string;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  storefrontLinkColor: string | null;
  backgroundColor: string | null;
  cardColor: string | null;
  borderColor: string | null;
  mutedTextColor: string | null;
  surfaceSoftColor: string | null;
  highlightColor?: string | null;
  logoFileId: string | null;
  faviconFileId: string | null;
  source: string;
  cssVariables: Record<string, string>;
}

export interface UpsertStoreBrandingPayload {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  storefrontLinkColor?: string | null;
  backgroundColor?: string | null;
  cardColor?: string | null;
  borderColor?: string | null;
  mutedTextColor?: string | null;
  surfaceSoftColor?: string | null;
  highlightColor?: string | null;
  logoFileId?: string | null;
  faviconFileId?: string | null;
}

export interface StoreBrandingUploadResult {
  fileId: string;
  fileName: string;
  contentType: string;
  length: number;
}

export interface StorefrontProfileSocialLinks {
  instagram?: string | null;
  youTube?: string | null;
  telegram?: string | null;
  linkedIn?: string | null;
  twitter?: string | null;
  website?: string | null;
}

export interface StorefrontProfileHighlight {
  title: string;
  body: string;
}

export interface StorefrontProfileFaqItem {
  question: string;
  answer: string;
  category?: string | null;
}

export interface StorefrontProfileContactMethod {
  title: string;
  body: string;
  actionLabel?: string | null;
  actionUrl?: string | null;
}

export interface StorefrontProfileLink {
  label: string;
  url: string;
}

export interface StorefrontContentProfile {
  brandName?: string | null;
  tagline?: string | null;
  shortDescription?: string | null;
  phones: string[];
  emails: string[];
  address?: string | null;
  hours?: string | null;
  socialLinks?: StorefrontProfileSocialLinks | null;
  aboutHeadline?: string | null;
  aboutBody?: string | null;
  aboutHighlights: StorefrontProfileHighlight[];
  faqItems: StorefrontProfileFaqItem[];
  contactSupportText?: string | null;
  contactMethods: StorefrontProfileContactMethod[];
  headerTopBarMessage?: string | null;
  headerQuickLinks: StorefrontProfileLink[];
  footerBrandText?: string | null;
  footerQuickLinks: StorefrontProfileLink[];
  footerCopyright?: string | null;
}

export interface StorefrontProfileResponse {
  storeId?: string | null;
  profile: StorefrontContentProfile;
  source: string;
}

export interface GenerateStorefrontProfileDraftPayload {
  brandDescription: string;
  industry?: string | null;
  audience?: string | null;
  tone?: string | null;
  language?: string | null;
}

export interface GenerateStorefrontProfileDraftResponse {
  profile: StorefrontContentProfile;
  provider: string;
  usedFallback: boolean;
  metadata: StorefrontProfileMetadata;
  guardrailViolations: StorefrontProfileGuardrailViolation[];
  requiresApproval: boolean;
}

export interface ComposeStorefrontGrowthPromptDiagnostic {
  sectionKey: string;
  status: string;
  message?: string | null;
}

export interface ComposeStorefrontGrowthPromptResponse {
  prompt: string;
  suggestedIndustry?: string | null;
  suggestedAudience?: string | null;
  suggestedTone?: string | null;
  suggestedLanguage?: string | null;
  hasGrowthData: boolean;
  hasCurrentProfileData: boolean;
  hasSourceData: boolean;
  completenessPercent: number;
  diagnostics: ComposeStorefrontGrowthPromptDiagnostic[];
}

export interface StorefrontProfileSeoSettings {
  includeOrganization: boolean;
  includeFaqPage: boolean;
  includeContactPage: boolean;
}

export interface StorefrontCompliancePack {
  region: string;
  privacyNotice?: string | null;
  legalDisclaimer?: string | null;
  regionalDisclaimer?: string | null;
  legalEntityId?: string | null;
  contactComplianceText?: string | null;
}

export interface StorefrontProfileAbExperimentConfig {
  status: string;
  trafficSplitPercent: number;
  heroOrderVariantB?: string | null;
  ctaVariantB?: string | null;
  faqOrderVariantB?: string | null;
}

export interface StorefrontAccessibilityContrastIssue {
  pair: string;
  foregroundColor: string;
  backgroundColor: string;
  ratio: number;
  requiredRatio: number;
  severity: string;
}

export interface StorefrontAccessibilityReport {
  passesWcagAa: boolean;
  issues: StorefrontAccessibilityContrastIssue[];
}

export interface StorefrontProfileGuardrailViolation {
  code: string;
  message: string;
  field?: string | null;
}

export interface StorefrontProfileMetadata {
  seo: StorefrontProfileSeoSettings;
  compliance?: StorefrontCompliancePack | null;
  abExperiment?: StorefrontProfileAbExperimentConfig | null;
  accessibility?: StorefrontAccessibilityReport | null;
}

export interface StorefrontProfileVersionSummary {
  id: string;
  locale: string;
  versionNumber: number;
  status: string;
  approvalStatus: string;
  isAiGenerated: boolean;
  cacheVersion: string;
  publishedAtUtc?: string | null;
  rolledBackFromVersionId?: string | null;
  createdAtUtc: string;
}

export interface StorefrontProfileDraftResponse {
  versionId?: string | null;
  locale: string;
  status: string;
  approvalStatus: string;
  isAiGenerated: boolean;
  approvalNotes?: string | null;
  profile: StorefrontContentProfile;
  metadata: StorefrontProfileMetadata;
  guardrailViolations: StorefrontProfileGuardrailViolation[];
}

export interface StorefrontProfilePublishedResponse {
  storeId?: string | null;
  locale: string;
  resolvedLocale: string;
  cacheVersion: string;
  profile: StorefrontContentProfile;
  metadata: StorefrontProfileMetadata;
  source: string;
  complianceRegion?: string | null;
}

export interface SaveStorefrontProfileDraftPayload {
  locale?: string | null;
  profile: StorefrontContentProfile;
  metadata?: StorefrontProfileMetadata | null;
  isAiGenerated?: boolean;
}

export interface StorefrontProfileApprovalPayload {
  notes?: string | null;
}

export interface StorefrontProfileRollbackPayload {
  versionId: string;
  locale?: string | null;
}

export interface StoreDomainBinding {
  id: string;
  storeId: string;
  host: string;
  pathBase: string;
  isPrimary: boolean;
  status: string;
}

export interface UpsertStoreDomainBindingPayload {
  host: string;
  pathBase?: string | null;
  isPrimary: boolean;
  status?: string | null;
}

export interface MarketplaceSellerOfferSummary {
  id: string;
  sellerId: string;
  offeringId: string;
  price: number;
  currencyCode: string;
  fulfillmentSlaHours?: number;
  isActive: boolean;
  storeId?: string | null;
}

export interface MarketplaceSellerOrderList {
  items: MarketplaceSellerOrder[];
  totalCount: number;
  page: number;
  pageSize: number;
  hasNext: boolean;
}

export interface MarketplaceSellerApplicationDocument {
  id: string;
  sellerId: string;
  documentType: string;
  status: string;
  blobReferenceId: string;
  version: number;
  reviewNotes: string;
  reviewActorReference: string;
  reviewedAtUtc: string | null;
  createdAtUtc: string;
}

export interface MarketplaceSellerOrder {
  id: string;
  offeringId: string;
  marketplaceSellerOfferId: string | null;
  grossAmount: number;
  currencyCode: string;
  status: string;
  fulfillmentChannel: string;
  createdAtUtc: string;
  fulfillmentReferenceId: string | null;
  fulfillmentLines: MarketplaceSellerFulfillmentLine[];
}

export interface MarketplaceSellerFulfillmentLine {
  id: string;
  lineNumber: number;
  offeringId: string;
  quantity: number;
  channel: string;
  status: string;
  fulfillmentReferenceId: string | null;
}

export interface StorefrontPublication {
  id: string;
  storeId: string;
  offeringId: string;
  categoryId: string | null;
  status: string;
  publishedAtUtc: string | null;
  visibleFromUtc: string | null;
  visibleToUtc: string | null;
  displayOrder: number;
  badgesJson: string;
  seoOverrideJson: string;
  merchandisingTagsJson: string;
  isPlatformDirectSaleAllowed: boolean;
}

export interface UpsertStorefrontPublicationPayload {
  storeId: string;
  offeringId: string;
  categoryId: string | null;
  visibleFromUtc: string | null;
  visibleToUtc: string | null;
  displayOrder: number;
  badgesJson?: string | null;
  seoOverrideJson?: string | null;
  merchandisingTagsJson?: string | null;
  isPlatformDirectSaleAllowed: boolean;
}

export interface StorefrontCategoryPlacement {
  id: string;
  storeId: string;
  categoryId: string;
  isVisible: boolean;
  isFeatured: boolean;
  showInNavigation: boolean;
  displayOrder: number;
  status: string;
}

export interface UpsertStorefrontCategoryPlacementPayload {
  storeId: string;
  categoryId: string;
  isVisible: boolean;
  isFeatured: boolean;
  showInNavigation: boolean;
  displayOrder: number;
  status?: string | null;
}

export interface ReorderCategoryPlacementPayload {
  placementId: string;
  displayOrder: number;
}

export interface StorefrontSection {
  id: string;
  storeId: string;
  placementKey: string;
  title: string;
  mode: string;
  ruleKey: string;
  ruleConfigurationJson: string;
  status: string;
  displayOrder: number;
  publishedAtUtc: string | null;
}

export interface UpsertStorefrontSectionPayload {
  storeId: string;
  placementKey: string;
  title: string;
  mode: string;
  ruleKey?: string | null;
  ruleConfigurationJson?: string | null;
  displayOrder: number;
  status?: string | null;
}

export interface ReorderSectionPayload {
  sectionId: string;
  displayOrder: number;
}

export interface StorefrontSectionItem {
  id: string;
  storeId: string;
  sectionId: string;
  offeringId: string | null;
  categoryId: string | null;
  sellerId: string | null;
  contentBlockId: string | null;
  navigationItemId: string | null;
  displayOrder: number;
  status: string;
  customDataJson: string;
}

export interface UpsertStorefrontSectionItemPayload {
  sectionId: string;
  offeringId: string | null;
  categoryId: string | null;
  sellerId: string | null;
  contentBlockId: string | null;
  navigationItemId: string | null;
  displayOrder: number;
  status?: string | null;
  customDataJson?: string | null;
}

export interface ReorderSectionItemPayload {
  itemId: string;
  displayOrder: number;
}

export interface StorefrontNavigationItem {
  id: string;
  storeId: string;
  locationKey: string;
  parentItemId: string | null;
  labelJson: string;
  targetType: string;
  targetValue: string;
  isVisible: boolean;
  displayOrder: number;
  status: string;
}

export interface UpsertStorefrontNavigationItemPayload {
  storeId: string;
  locationKey: string;
  parentItemId: string | null;
  labelJson: string;
  targetType: string;
  targetValue: string;
  isVisible: boolean;
  displayOrder: number;
  status?: string | null;
}

export interface ReorderNavigationItemPayload {
  itemId: string;
  displayOrder: number;
}

export interface StorefrontContentBlock {
  id: string;
  storeId: string;
  placementKey: string;
  title: string;
  body: string;
  contentFormat: string;
  status: string;
  visibleFromUtc: string | null;
  visibleToUtc: string | null;
  displayOrder: number;
  publishedAtUtc: string | null;
}

export interface UpsertStorefrontContentBlockPayload {
  storeId: string;
  placementKey: string;
  title: string;
  body: string;
  contentFormat: string;
  visibleFromUtc: string | null;
  visibleToUtc: string | null;
  displayOrder: number;
  status?: string | null;
}

export interface CatalogOperationsPostingProfile {
  id: string;
  tenantId: string;
  isEnabled: boolean;
  defaultCurrencyCode: string;
  serviceRevenueDebitAccountId: string | null;
  serviceRevenueCreditAccountId: string | null;
  servicePartsCostDebitAccountId: string | null;
  servicePartsCostCreditAccountId: string | null;
  rmaRefundDebitAccountId: string | null;
  rmaRefundCreditAccountId: string | null;
  warrantyExpenseDebitAccountId: string | null;
  warrantyExpenseCreditAccountId: string | null;
  assetCapitalizationDebitAccountId: string | null;
  assetCapitalizationCreditAccountId: string | null;
  assetDepreciationExpenseDebitAccountId: string | null;
  assetDepreciationCreditAccountId: string | null;
  assetDisposalGainLossDebitAccountId: string | null;
  assetDisposalGainLossCreditAccountId: string | null;
  rentalRevenueDebitAccountId: string | null;
  rentalRevenueCreditAccountId: string | null;
  rentalDepositDebitAccountId: string | null;
  rentalDepositCreditAccountId: string | null;
  rentalDamageChargeDebitAccountId: string | null;
  rentalDamageChargeCreditAccountId: string | null;
  cmmsMaintenanceExpenseDebitAccountId: string | null;
  cmmsMaintenanceExpenseCreditAccountId: string | null;
  cmmsSparePartConsumptionDebitAccountId: string | null;
  cmmsSparePartConsumptionCreditAccountId: string | null;
  hospitalityPrepaymentDebitAccountId: string | null;
  hospitalityPrepaymentCreditAccountId: string | null;
  hospitalityFolioChargeDebitAccountId: string | null;
  hospitalityFolioChargeCreditAccountId: string | null;
  hospitalityRefundDebitAccountId: string | null;
  hospitalityRefundCreditAccountId: string | null;
  hospitalityMinibarCogsDebitAccountId: string | null;
  hospitalityMinibarCogsCreditAccountId: string | null;
  hospitalityChannelCommissionDebitAccountId: string | null;
  hospitalityChannelCommissionCreditAccountId: string | null;
  inventoryIssueDebitAccountId: string | null;
  inventoryIssueCreditAccountId: string | null;
  inventoryCogsDebitAccountId: string | null;
  inventoryCogsCreditAccountId: string | null;
  inventoryReceiptDebitAccountId: string | null;
  inventoryReceiptCreditAccountId: string | null;
  inventoryAdjustmentGainDebitAccountId: string | null;
  inventoryAdjustmentGainCreditAccountId: string | null;
  inventoryAdjustmentLossDebitAccountId: string | null;
  inventoryAdjustmentLossCreditAccountId: string | null;
  inventoryLandedCostDebitAccountId: string | null;
  inventoryLandedCostCreditAccountId: string | null;
  inventoryRevaluationGainDebitAccountId: string | null;
  inventoryRevaluationGainCreditAccountId: string | null;
  inventoryRevaluationLossDebitAccountId: string | null;
  inventoryRevaluationLossCreditAccountId: string | null;
  inventoryStandardVarianceGainDebitAccountId: string | null;
  inventoryStandardVarianceGainCreditAccountId: string | null;
  inventoryStandardVarianceLossDebitAccountId: string | null;
  inventoryStandardVarianceLossCreditAccountId: string | null;
  inventoryTransferDebitAccountId: string | null;
  inventoryTransferCreditAccountId: string | null;
}

export interface UpsertCatalogOperationsPostingProfilePayload {
  isEnabled: boolean;
  defaultCurrencyCode: string;
  serviceRevenueDebitAccountId: string | null;
  serviceRevenueCreditAccountId: string | null;
  servicePartsCostDebitAccountId: string | null;
  servicePartsCostCreditAccountId: string | null;
  rmaRefundDebitAccountId: string | null;
  rmaRefundCreditAccountId: string | null;
  warrantyExpenseDebitAccountId: string | null;
  warrantyExpenseCreditAccountId: string | null;
  assetCapitalizationDebitAccountId: string | null;
  assetCapitalizationCreditAccountId: string | null;
  assetDepreciationExpenseDebitAccountId: string | null;
  assetDepreciationCreditAccountId: string | null;
  assetDisposalGainLossDebitAccountId: string | null;
  assetDisposalGainLossCreditAccountId: string | null;
  rentalRevenueDebitAccountId: string | null;
  rentalRevenueCreditAccountId: string | null;
  rentalDepositDebitAccountId: string | null;
  rentalDepositCreditAccountId: string | null;
  rentalDamageChargeDebitAccountId: string | null;
  rentalDamageChargeCreditAccountId: string | null;
  cmmsMaintenanceExpenseDebitAccountId: string | null;
  cmmsMaintenanceExpenseCreditAccountId: string | null;
  cmmsSparePartConsumptionDebitAccountId: string | null;
  cmmsSparePartConsumptionCreditAccountId: string | null;
  hospitalityPrepaymentDebitAccountId: string | null;
  hospitalityPrepaymentCreditAccountId: string | null;
  hospitalityFolioChargeDebitAccountId: string | null;
  hospitalityFolioChargeCreditAccountId: string | null;
  hospitalityRefundDebitAccountId: string | null;
  hospitalityRefundCreditAccountId: string | null;
  hospitalityMinibarCogsDebitAccountId: string | null;
  hospitalityMinibarCogsCreditAccountId: string | null;
  hospitalityChannelCommissionDebitAccountId: string | null;
  hospitalityChannelCommissionCreditAccountId: string | null;
  inventoryIssueDebitAccountId: string | null;
  inventoryIssueCreditAccountId: string | null;
  inventoryCogsDebitAccountId: string | null;
  inventoryCogsCreditAccountId: string | null;
  inventoryReceiptDebitAccountId: string | null;
  inventoryReceiptCreditAccountId: string | null;
  inventoryAdjustmentGainDebitAccountId: string | null;
  inventoryAdjustmentGainCreditAccountId: string | null;
  inventoryAdjustmentLossDebitAccountId: string | null;
  inventoryAdjustmentLossCreditAccountId: string | null;
  inventoryLandedCostDebitAccountId: string | null;
  inventoryLandedCostCreditAccountId: string | null;
  inventoryRevaluationGainDebitAccountId: string | null;
  inventoryRevaluationGainCreditAccountId: string | null;
  inventoryRevaluationLossDebitAccountId: string | null;
  inventoryRevaluationLossCreditAccountId: string | null;
  inventoryStandardVarianceGainDebitAccountId: string | null;
  inventoryStandardVarianceGainCreditAccountId: string | null;
  inventoryStandardVarianceLossDebitAccountId: string | null;
  inventoryStandardVarianceLossCreditAccountId: string | null;
  inventoryTransferDebitAccountId: string | null;
  inventoryTransferCreditAccountId: string | null;
}

export interface CatalogFinancialPostingRecord {
  id: string;
  eventType: string;
  sourceEntityId: string;
  amount: number;
  currencyCode: string;
  status: string;
  journalEntryId: string | null;
  errorCode: string;
  createdAtUtc: string;
}

export interface ConsumeServicePartsPayload {
  offeringId: string;
  lotId: string | null;
  serialId: string | null;
  quantity: number;
  referenceNumber: string;
  unitCost: number;
}

export interface CompleteServiceOrderPayload {
  completionNotes: string;
  checklistResultsJson: string;
  requiresCustomerApproval: boolean;
  serviceRevenueAmount: number;
}

export interface AssetMaster {
  id: string;
  assetNumber: string;
  name: string;
  assetCategoryId: string;
  assetBookId: string;
  costCenterCode: string;
  locationCode: string;
  status: string;
  acquisitionDate: string;
  inServiceDate: string | null;
  acquisitionCost: number;
  accumulatedDepreciation: number;
  netBookValue: number;
  depreciationMethod: string;
  usefulLifeMonths: number;
  salvageValue: number;
  isMaintenanceLocked: boolean;
  insurancePolicies?: AssetInsurancePolicy[];
}

export interface AssetCategory {
  id: string;
  code: string;
  name: string;
  parentCategoryId: string | null;
}

export interface AssetBook {
  id: string;
  code: string;
  name: string;
  currencyCode: string;
  defaultDepreciationMethod: string;
}

export interface RegisterAssetPayload {
  assetNumber: string;
  name: string;
  assetCategoryId: string;
  assetBookId: string;
  offeringId: string | null;
  serialId: string | null;
  costCenterCode: string;
  locationCode: string;
  acquisitionDate: string;
  acquisitionCost: number;
  depreciationMethod: string;
  usefulLifeMonths: number;
  salvageValue: number;
  decliningBalanceRatePercent: number;
  unitsOfProductionCapacity: number;
  placeInService: boolean;
}

export interface CreateAssetCategoryPayload {
  code: string;
  name: string;
  parentCategoryId: string | null;
}

export interface CreateAssetBookPayload {
  code: string;
  name: string;
  currencyCode: string;
  defaultDepreciationMethod: string;
}

export interface RunDepreciationPayload {
  assetBookId: string;
  runNumber: string;
  periodEndDate: string;
  batchSize: number;
  enqueueOnly: boolean;
}

export interface AssetDepreciationRun {
  id: string;
  assetBookId: string;
  runNumber: string;
  periodEndDate: string;
  status: string;
  totalDepreciationAmount: number;
  processedAssetCount: number;
  totalAssetCount: number;
}

export interface RentalAgreement {
  id: string;
  rentalBookingId: string | null;
  rentalAssetUnitId: string;
  agreementNumber: string;
  customerReference: string;
  startUtc: string;
  endUtc: string;
  depositAmount: number;
  rentalRateAmount: number;
  status: string;
  handover?: RentalHandoverChecklist | null;
  returnChecklist?: RentalReturnChecklist | null;
  damageAssessment?: RentalDamageAssessment | null;
  settlement?: RentalSettlement | null;
}

export interface RentalHandoverChecklist {
  id: string;
  rentalAgreementId: string;
  checklistJson: string;
  photoEvidenceJson: string;
  signatureReference: string;
  handoverAtUtc: string;
}

export interface RentalReturnChecklist {
  id: string;
  rentalAgreementId: string;
  checklistJson: string;
  photoEvidenceJson: string;
  signatureReference: string;
  returnAtUtc: string;
}

export interface RentalDamageAssessment {
  id: string;
  rentalAgreementId: string;
  damageAmount: number;
  description: string;
  status: string;
}

export interface RentalDamageAssessmentPayload {
  damageAmount: number;
  description: string;
  photoEvidenceJson: string;
  approvalThresholdAmount: number;
}

export interface RentalAssetUnit {
  id: string;
  unitCode: string;
  name: string;
  assetId: string | null;
  offeringId: string | null;
  status: string;
}

export interface CreateRentalAgreementPayload {
  rentalBookingId: string | null;
  rentalAssetUnitId: string;
  agreementNumber: string;
  customerReference: string;
  startUtc: string;
  endUtc: string;
  depositAmount: number;
  rentalRateAmount: number;
}

export interface RentalHandoverPayload {
  checklistJson: string;
  photoEvidenceJson: string;
  signatureReference: string;
}

export interface RentalReturnPayload {
  checklistJson: string;
  photoEvidenceJson: string;
  signatureReference: string;
  actualReturnUtc: string;
  lateFeeRuleId: string | null;
}

export interface RentalSettlePayload {
  rentalCharges: number;
  lateFeeAmount: number;
  damageChargeAmount: number;
  depositApplied: number;
}

export interface RentalSettlement {
  id: string;
  rentalAgreementId: string;
  rentalCharges: number;
  lateFeeAmount: number;
  damageChargeAmount: number;
  depositApplied: number;
  balanceDue: number;
}

export interface WorkOrder {
  id: string;
  assetId: string;
  maintenancePlanId: string | null;
  workOrderNumber: string;
  title: string;
  workOrderType: string;
  status: string;
  assignedResourceId: string | null;
  requiresPostApproval: boolean;
  startedAtUtc: string | null;
  completedAtUtc: string | null;
  completionNotes: string;
  consumptions?: WorkOrderConsumption[] | null;
  spareReservations?: SparePartReservation[] | null;
}

export interface WorkOrderConsumption {
  id: string;
  workOrderId: string;
  offeringId: string;
  lotId: string | null;
  serialId: string | null;
  quantity: number;
  referenceNumber: string;
}

export interface ConsumeWorkOrderPartsPayload {
  offeringId: string;
  warehouseId: string;
  binId?: string | null;
  lotId: string | null;
  serialId: string | null;
  quantity: number;
  referenceNumber: string;
  unitCost: number;
}

export interface ReserveWorkOrderPartsPayload {
  offeringId: string;
  warehouseId: string;
  binId?: string | null;
  quantity: number;
}

export interface ExplodeWorkOrderBomPayload {
  parentOfferingId: string;
  warehouseId: string;
  binId?: string | null;
  parentQuantity: number;
}

export interface SparePartReservation {
  id: string;
  workOrderId: string;
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  inventoryReservationId: string | null;
  quantity: number;
  status: string;
}

export interface OutboundShipment {
  id: string;
  warehouseTaskId: string;
  offeringId: string;
  warehouseId: string;
  referenceNumber: string;
  deliveryStatus: string;
  carrierCode: string | null;
  carrierName: string | null;
  trackingNumber: string | null;
  vehicleRef: string | null;
  driverRef: string | null;
  dispatchAt: string | null;
  createdAtUtc: string;
}

export interface ConfirmOutboundShipmentPayload {
  idempotencyKey: string;
  carrierCode?: string | null;
  carrierName?: string | null;
  trackingNumber?: string | null;
  vehicleRef?: string | null;
  driverRef?: string | null;
  dispatchAt?: string | null;
}

export interface CarrierWebhookEvent {
  id: string;
  externalEventId: string;
  carrierCode: string;
  mappedDeliveryStatus: string;
  outboundShipmentId: string | null;
  trackingNumber: string | null;
  processingStatus: string;
  processingNotes: string | null;
  createdAtUtc: string;
}

export interface CmmsBomPreview {
  parentOfferingId: string;
  parentQuantity: number;
  components: { componentOfferingId: string; quantityPerUnit: number; totalQuantity: number }[];
}

export interface CreateWorkOrderPayload {
  assetId: string;
  maintenancePlanId: string | null;
  workOrderNumber: string;
  title: string;
  workOrderType: string;
  requiresPostApproval: boolean;
}

export interface CompleteWorkOrderPayload {
  completionNotes: string;
  maintenanceExpenseAmount: number | null;
}

export interface DowntimeDashboard {
  openDowntimeCount: number;
  totalDowntimeHoursLast30Days: number;
}

export interface RentalBooking {
  id: string;
  rentalAssetUnitId: string;
  bookingReference: string;
  customerReference: string;
  startUtc: string;
  endUtc: string;
  status: string;
}

export interface CreateRentalBookingPayload {
  rentalAssetUnitId: string;
  bookingReference: string;
  customerReference: string;
  startUtc: string;
  endUtc: string;
}

export interface RentalBookingBoardEntry {
  rentalAssetUnitId: string;
  unitCode: string;
  unitName: string;
  unitStatus: string;
  bookings: RentalBooking[];
  agreements: RentalAgreement[];
}

export interface HospitalityBookingBoardReservation {
  id: string;
  reservationReference: string;
  customerReference: string;
  checkInDateUtc: string;
  checkOutDateUtc: string;
  status: string;
  roomUnitId: string | null;
  roomTypeId: string;
  roomTypeCode: string;
}

export interface HospitalityBookingBoardEntry {
  rowId: string;
  rowCode: string;
  rowLabel: string;
  rowKind: string;
  rowStatus: string | null;
  reservations: HospitalityBookingBoardReservation[];
}

export interface MaintenancePlan {
  id: string;
  assetId: string;
  planCode: string;
  name: string;
  triggerType: string;
  intervalDays: number;
  meterInterval: number;
  eventCode: string;
  nextDueDate: string | null;
  nextDueMeter: number | null;
  isActive: boolean;
}

export interface CreateMaintenancePlanPayload {
  assetId: string;
  planCode: string;
  name: string;
  triggerType: string;
  intervalDays: number;
  meterInterval: number;
  eventCode: string;
  nextDueDate: string | null;
  nextDueMeter: number | null;
}

export interface AssetInsurancePolicy {
  id: string;
  assetId: string;
  insurerName: string;
  policyNumber: string;
  coverageAmount: number;
  effectiveDate: string;
  expiryDate: string;
}

export interface AddAssetInsurancePayload {
  insurerName: string;
  policyNumber: string;
  coverageAmount: number;
  effectiveDate: string;
  expiryDate: string;
}

export interface LegacyAssetImportRowPayload {
  legacyAssetNumber: string;
  name: string;
  categoryCode: string;
  bookCode: string;
  costCenterCode: string;
  locationCode: string;
  acquisitionDate: string;
  acquisitionCost: number;
  depreciationMethod: string;
  usefulLifeMonths: number;
  salvageValue: number;
  createRentalUnit: boolean;
  legacyMaintenanceLogRef?: string | null;
}

export interface LegacyAssetImportPayload {
  dryRun: boolean;
  rows: LegacyAssetImportRowPayload[];
}

export interface LegacyAssetImportRowResult {
  rowIndex: number;
  legacyAssetNumber: string;
  success: boolean;
  assetId: string | null;
  rentalUnitId: string | null;
  error: string | null;
}

export interface LegacyAssetImportResult {
  dryRun: boolean;
  totalRows: number;
  successCount: number;
  failureCount: number;
  results: LegacyAssetImportRowResult[];
}

export interface HospitalityProperty {
  id: string;
  propertyCode: string;
  name: string;
  address: string;
  timeZoneId: string;
  isActive: boolean;
}

export interface CreateHospitalityPropertyPayload {
  propertyCode: string;
  name: string;
  address: string;
  timeZoneId?: string;
}

export interface UpdateHospitalityPropertyPayload {
  name: string;
  address: string;
  timeZoneId?: string;
  isActive: boolean;
}

export interface HospitalityRoomType {
  id: string;
  propertyId: string;
  typeCode: string;
  name: string;
  maxOccupancy: number;
  baseNightlyRate: number;
}

export interface CreateHospitalityRoomTypePayload {
  propertyId: string;
  typeCode: string;
  name: string;
  maxOccupancy: number;
  baseNightlyRate: number;
}

export interface HospitalityRoomUnit {
  id: string;
  propertyId: string;
  roomTypeId: string;
  roomNumber: string;
  floor: string | null;
  status: string;
}

export interface CreateHospitalityRoomPayload {
  propertyId: string;
  roomTypeId: string;
  roomNumber: string;
  floor?: string | null;
}

export interface HospitalityAvailabilityQuery {
  propertyId: string;
  roomTypeId: string;
  checkInDateUtc: string;
  checkOutDateUtc: string;
  ratePlanId?: string;
}

export interface HospitalityRatePlan {
  id: string;
  propertyId: string;
  planCode: string;
  name: string;
  currencyCode: string;
  isActive: boolean;
}

export interface CreateHospitalityRatePlanPayload {
  propertyId: string;
  planCode: string;
  name: string;
  currencyCode?: string;
}

export interface HospitalityRateCalendarQuery {
  propertyId: string;
  ratePlanId?: string;
  roomTypeId?: string;
  fromUtc?: string;
  toUtc?: string;
}

export interface HospitalityRateCalendarEntry {
  id: string;
  ratePlanId: string;
  roomTypeId: string;
  effectiveFromUtc: string;
  effectiveToUtc: string;
  nightlyRate: number;
}

export interface CreateHospitalityRateCalendarEntryPayload {
  ratePlanId: string;
  roomTypeId: string;
  effectiveFromUtc: string;
  effectiveToUtc: string;
  nightlyRate: number;
}

export interface HospitalityFrontDeskBoard {
  propertyId: string;
  generatedAtUtc: string;
  statusSummary: HospitalityFrontDeskStatusSummary[];
  rooms: HospitalityFrontDeskRoom[];
}

export interface HospitalityFrontDeskStatusSummary {
  status: string;
  count: number;
}

export interface HospitalityFrontDeskRoom {
  roomUnitId: string;
  roomNumber: string;
  floor: string | null;
  status: string;
  roomTypeId: string;
  roomTypeCode: string;
  roomTypeName: string;
  occupancy: HospitalityFrontDeskOccupancy | null;
  pendingHousekeeping: HospitalityFrontDeskHousekeeping | null;
}

export interface HospitalityFrontDeskOccupancy {
  stayId: string;
  reservationId: string;
  reservationReference: string;
  customerReference: string;
  checkInUtc: string;
  checkOutUtc: string | null;
  reservationCheckOutUtc: string;
  folioBalanceDue: number | null;
}

export interface HospitalityFrontDeskHousekeeping {
  taskId: string;
  taskType: string;
  status: string;
  dueAtUtc: string;
}

export interface HospitalityAvailability {
  propertyId: string;
  roomTypeId: string;
  totalRooms: number;
  availableRooms: number;
  reservedRooms: number;
  suggestedNightlyRate: number;
  estimatedStayTotal: number;
  nights: number;
}

export interface HospitalityReservation {
  id: string;
  propertyId: string;
  roomTypeId: string;
  roomUnitId: string | null;
  ratePlanId: string | null;
  reservationReference: string;
  customerReference: string;
  checkInDateUtc: string;
  checkOutDateUtc: string;
  guestCount: number;
  estimatedTotal: number;
  status: string;
  financialSummary: HospitalityReservationFinancialSummary | null;
}

export interface HospitalityReservationFinancialSummary {
  prepaymentReceived: number;
  prepaymentRefunded: number;
  prepaymentApplied: number;
  prepaymentAvailable: number;
  channelCommissionAmount: number;
  channelCode: string | null;
}

export interface ReceiveHospitalityPrepaymentPayload {
  amount: number;
  idempotencyKey: string;
}

export interface PostHospitalityRefundPayload {
  amount: number;
  reason: string;
  idempotencyKey: string;
}

export interface PostHospitalityChannelCommissionPayload {
  channelCode: string;
  amount: number;
  idempotencyKey: string;
}

export interface HospitalityPrepayment {
  id: string;
  reservationId: string;
  receivedAmount: number;
  refundedAmount: number;
  appliedAmount: number;
  availableAmount: number;
}

export interface HospitalityRefund {
  id: string;
  reservationId: string;
  amount: number;
  reason: string;
}

export interface HospitalityChannelCommission {
  id: string;
  reservationId: string;
  channelCode: string;
  amount: number;
}

export interface CreateHospitalityReservationPayload {
  propertyId: string;
  roomTypeId: string;
  roomUnitId?: string | null;
  ratePlanId?: string | null;
  reservationReference: string;
  customerReference: string;
  checkInDateUtc: string;
  checkOutDateUtc: string;
  guestCount: number;
  idempotencyKey: string;
}

export interface HospitalityCheckInPayload {
  roomUnitId?: string | null;
}

export interface HospitalityFolioLine {
  id: string;
  chargeType: string;
  description: string;
  amount: number;
  postedAtUtc: string;
}

export interface HospitalityFolioSummary {
  id: string;
  status: string;
  balanceDue: number;
  lines: HospitalityFolioLine[];
}

export interface HospitalityStay {
  id: string;
  reservationId: string;
  roomUnitId: string;
  checkInUtc: string;
  checkOutUtc: string | null;
  status: string;
  folio: HospitalityFolioSummary | null;
}

export interface PostHospitalityFolioChargePayload {
  chargeType: string;
  description: string;
  amount: number;
}

export interface HospitalityCheckOutResult {
  stayId: string;
  folioId: string;
  balanceDue: number;
  housekeepingTaskId: string;
  roomStatus: string;
}

export interface HousekeepingTask {
  id: string;
  roomUnitId: string;
  taskType: string;
  status: string;
  assignedToReference: string;
  dueAtUtc: string;
  completedAtUtc: string | null;
}

export interface HousekeepingTaskDetail extends HousekeepingTask {
  roomNumber: string;
  floor: string | null;
}

export interface HospitalityCancellationPolicy {
  propertyId: string;
  freeCancelHoursBeforeCheckIn: number;
  cancellationFeePercent: number;
  noShowFeePercent: number;
  isEnabled: boolean;
}

export interface UpdateHospitalityCancellationPolicyPayload {
  freeCancelHoursBeforeCheckIn: number;
  cancellationFeePercent: number;
  noShowFeePercent: number;
  isEnabled: boolean;
}

export interface HospitalityOverbookingPolicy {
  propertyId: string;
  allowedOverbookPercent: number;
  isEnabled: boolean;
}

export interface UpdateHospitalityOverbookingPolicyPayload {
  allowedOverbookPercent: number;
  isEnabled: boolean;
}

export interface CancelHospitalityReservationPayload {
  reason?: string | null;
  policyExceptionApproved: boolean;
}

export interface MarkHospitalityNoShowPayload {
  reason?: string | null;
  policyExceptionApproved: boolean;
}

export interface HospitalityAssignableRoom {
  roomUnitId: string;
  roomNumber: string;
  floor: string | null;
  status: string;
}

export interface HospitalityChannelRateRule {
  id: string;
  propertyId: string;
  ratePlanId: string;
  channelCode: string;
  rateAdjustmentPercent: number;
  allocatedRooms: number | null;
  isActive: boolean;
}

export interface CreateHospitalityChannelRateRulePayload {
  propertyId: string;
  ratePlanId: string;
  channelCode: string;
  rateAdjustmentPercent: number;
  allocatedRooms?: number | null;
}

export interface UpdateHospitalityChannelRateRulePayload {
  rateAdjustmentPercent: number;
  allocatedRooms?: number | null;
  isActive: boolean;
}

export interface PostMiniBarConsumptionPayload {
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
}

export interface MiniBarConsumption {
  id: string;
  stayId: string;
  roomUnitId: string;
  folioLineId: string | null;
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
  consumedAtUtc: string;
}

export interface CreateRoomMaintenanceRequestPayload {
  roomUnitId: string;
  issueDescription: string;
  createWorkOrder?: boolean;
  assetId?: string | null;
}

export interface RoomMaintenanceRequest {
  id: string;
  roomUnitId: string;
  roomNumber: string;
  issueDescription: string;
  status: string;
  workOrderId: string | null;
}

export interface HospitalityPolicyException {
  id: string;
  propertyId: string;
  exceptionType: string;
  referenceEntityName: string;
  referenceEntityId: string;
  reason: string;
  status: string;
  approvedByReference: string | null;
}

export interface CreateHospitalityPolicyExceptionPayload {
  propertyId: string;
  exceptionType: string;
  referenceEntityName: string;
  referenceEntityId: string;
  reason: string;
}

export interface ResolveHospitalityPolicyExceptionPayload {
  approve: boolean;
  approvedByReference: string;
}

export interface HospitalityKpi {
  propertyId: string;
  fromDate: string;
  toDate: string;
  totalRooms: number;
  occupiedRooms: number;
  occupancyRate: number;
  averageDailyRate: number;
  revenuePerAvailableRoom: number;
  pendingHousekeepingTasks: number;
  activeStays: number;
  confirmedReservations: number;
}

export interface RunHospitalityReconciliationPayload {
  propertyId: string;
  businessDate?: string | null;
}

export interface HospitalityReconciliation {
  id: string;
  propertyId: string;
  businessDate: string;
  folioChargesTotal: number;
  prepaymentsTotal: number;
  refundsTotal: number;
  postingVariance: number;
  status: string;
}

export interface HospitalityDemoSeedResult {
  propertyId: string;
  roomTypeId: string;
  ratePlanId: string;
  alreadySeeded: boolean;
}
